import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Search, Home, MapPin, Wallet, BedDouble, Building2, Users, Loader2,
    Target, Phone, Mail, ChevronDown, ChevronUp, ChevronRight, Check, X, Copy, Download,
    Sparkles, Compass, Bath, Car, Pencil, MessageSquare, Send, FileText,
    ImagePlus, Trash2, Eye, EyeOff, FlaskConical,
    Blocks, Code, LayoutTemplate, Save, Link2, AlertTriangle, Wallet2, StickyNote,
    ShoppingBag, Undo2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
    fetchFindBuyersListings,
    fetchFindBuyersSuburbs,
    findBuyersForProperty,
    editBuyerPreferences,
    messageBuyerMatches,
    sendTestBuyerMatchMessage,
    fetchPurchasedBuyers,
    setBuyerPurchased,
    setBuyerRented,
} from '../api/analyticsApi';
import RentedReturnPicker, { DEFAULT_RENTED_RETURN } from '../../contacts/components/RentedReturnPicker';
import BuyerActionsModal from '../../../components/properties/BuyerActionsModal';
import ContactNotesButton from '../../contacts/components/ContactNotesButton';
import { getLatestNotes } from '../../../api/contact';
import api from '../../../api';
import { prettyGnafAddress } from '../../../components/common/AddressAutocomplete';
import BuyerDemandReportModal from './BuyerDemandReportModal';
import { PropertyTypeTiles, SuburbPicker } from './BuyerPreferenceFields';
import SuburbAutocomplete from '../../../components/common/SuburbAutocomplete';
import { formatMelbourneDatePart, formatMelbourneDateTime, formatMelbourneShortDate } from '../../../utils/melbourneTime';
import {
    smsInfo, asSent, SMS_STOP_PLACEHOLDER, appendStopPlaceholder, withSmsIntro,
} from '../../communications/utils/smsCredits';
import useSmsSender, { COLLINGS_SENDER } from '../../communications/utils/useSmsSender';
import { derivePreviewFromBody } from '../../communications/utils/previewText';
// The composer shares its parts with the Communications tab so the two tools
// behave identically (CG, 2026-08-28).
import AiWritePanel from '../../communications/components/AiWritePanel';
import MessageExtras from '../../communications/components/MessageExtras';
import MessageBlocksTail from '../../communications/components/MessageBlocksTail';
import SmsIntroHead from '../../communications/components/SmsIntroHead';
import SmsSendFrom from '../../communications/components/SmsSendFrom';
import PropertyPickerPanel from '../../communications/components/PropertyPickerPanel';
import EmailBuilder from '../../communications/components/EmailBuilder';
import {
    fetchTemplates, createTemplate, fetchMergeTags, checkMergeTags,
    fetchPropertyEmailData, fetchSmsCreditBalance,
} from '../../communications/api/communicationsApi';
import {
    buildPropertyEmailHtml, propertyEmailMeta, propertyEmailIntro,
    buildPropertyJustListedSms,
} from '../../communications/utils/propertyEmail';
import TemplatePickerModal from '../../communications/components/TemplatePickerModal';
import { substituteTags, unresolvedTags, unresolvedSummary } from '../../communications/utils/mergeTags';
import { getAgents } from '../../../api/agent';
import {
    appendEmailBlocks,
    assembleSms,
    emailBlocksHtml,
    previewHtml,
    previewText,
    pricedText,
    buildSmsAgentBlock,
    stripSmsAgentBlock,
    OFF_MARKET_TAG,
    PREFERENCES_TAG,
} from '../../communications/utils/messageBlocks';
import { geocodePlace, enrichSuburbsWithDistance, hasGeocoder } from '../utils/geocode';
import { hasAdminAccess } from "../../../utils/roleAccess";
import BuyerStatusActions from "../../contacts/components/BuyerStatusActions";
import { MatchLevelChip, NextAction, ReasonChips, scoreTitle } from "./MatchLevel";
import { sanitizeHtml } from '../../../utils/sanitizeHtml';

// Our own GNAF address search, the same source the Add Property form uses first.
// Lets the agent find a property that isn't in Collings yet. (This used to call
// Addressr on RapidAPI directly, which shipped its key in the browser bundle.)
const searchAddresses = async (query) => {
    if (!query || query.length < 3) return [];
    try {
        const res = await api.get('/api/suburb-intelligence/address-autocomplete/', { params: { q: query } });
        const results = Array.isArray(res?.data?.results) ? res.data.results : [];
        return results.filter((r) => r && r.address).slice(0, 6).map((r) => ({
            description: prettyGnafAddress(r.address),
            raw: { gnaf: true, property_id: r.property_id },
        }));
    } catch {
        return [];
    }
};

const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

// Pull suburb / state / postcode out of a formatted AU address string. Addressr
// returns suburb + state + postcode together in the last segment, e.g.
// "1/38 Somerlayton Cr, Fawkner VIC 3060", so we anchor on the state token: the
// suburb is the last comma-segment before it, the postcode the trailing 4 digits.
const parseAddressString = (s) => {
    const str = (s || '').trim();
    let suburb = '', state = '', postcode = '';
    const pc = str.match(/\b(\d{4})\b(?![\s\S]*\b\d{4}\b)/); // last 4-digit group
    if (pc) postcode = pc[1];

    const stateRe = new RegExp(`\\b(${AU_STATES.join('|')})\\b`, 'ig');
    let m, lastState = null;
    while ((m = stateRe.exec(str)) !== null) lastState = m;
    if (lastState) {
        state = lastState[1].toUpperCase();
        const before = str.slice(0, lastState.index).replace(/,\s*$/, '').trim();
        suburb = before.split(',').pop().trim(); // segment immediately before the state
    } else if (str.includes(',')) {
        // No state token — best-effort: second-last comma segment.
        const parts = str.split(',').map((x) => x.trim()).filter(Boolean);
        if (parts.length >= 2) suburb = parts[parts.length - 1];
    }
    return { suburb, state, postcode };
};

const extractAddressFields = (sugg) => {
    const raw = sugg?.raw || {};
    const comp = raw.components || raw.address_components || {};
    let suburb = raw.suburb || raw.locality || raw.locality_name || raw.suburb_name || comp.suburb || comp.locality || '';
    let state = raw.state || raw.state_abbreviation || raw.state_code || raw.state_name || comp.state || '';
    let postcode = raw.postcode || raw.postal_code || comp.postcode || comp.postal_code || '';
    const lat = raw.latitude ?? raw.lat ?? raw.location?.lat ?? raw.geo?.lat ?? raw.geocoding?.latitude ?? null;
    const lng = raw.longitude ?? raw.lng ?? raw.lon ?? raw.location?.lon ?? raw.location?.lng ?? raw.geo?.lng ?? raw.geocoding?.longitude ?? null;
    if (!suburb || !state || !postcode) {
        const p = parseAddressString(sugg?.description || '');
        suburb = suburb || p.suburb; state = state || p.state; postcode = postcode || p.postcode;
    }
    return {
        suburb, state, postcode,
        lat: lat != null ? Number(lat) : null,
        lng: lng != null ? Number(lng) : null,
    };
};

/**
 * Find Buyers for a Property (Buyer Analytics tab)
 *
 * Christian's ask: "I want to find buyers for this property. Set requirements for
 * price, bedrooms, type, but nobody marked Fawkner exactly; they marked
 * surrounding suburbs, or just 'Melbourne'. We could still tick them to add them
 * to the list of people to contact."
 *
 * Flow:
 *   1. Pick a listing (prefills suburb/price/beds/type) or type filters by hand.
 *   2. Tick the suburbs that count: target, everything within the radius, and the
 *      capital city of the listing's state (e.g. "Melbourne") are pre-ticked; the
 *      agent can add or remove any suburb by hand.
 *   3. Rank the buyers whose saved preferences fit, tagged exact / surrounding /
 *      other, with a match %, intent score and reason chips. Click a buyer to log
 *      a call / set a reminder (surfaces in Who-to-Call), or shortlist + export.
 */

const RADIUS_OPTIONS = [2, 5, 10, 15, 25];

// How many result cards to paint per batch (see visibleCount below).
const ROWS_PER_BATCH = 100;

// Map a listing's Property.property_type onto a requirements tick-box option.
const LISTING_TYPE_TO_OPTION = {
    house: 'House', apartment: 'Apartment', unit: 'Unit', townhouse: 'Townhouse',
    villa: 'Villa', land: 'Land', blocks_of_units: 'Block of Units', house_and_land: 'House and Land',
};

const fmtMoney = (n) => {
    if (n == null || n === '') return '';
    const num = Number(n);
    if (Number.isNaN(num)) return '';
    return `$${num.toLocaleString('en-AU')}`;
};

const SUBURB_KIND = {
    exact: { label: 'This suburb', chip: 'bg-emerald-100 text-emerald-700' },
    surrounding: { label: 'Surrounding', chip: 'bg-sky-100 text-sky-700' },
    other: { label: 'Other areas', chip: 'bg-gray-100 text-gray-600' },
};

const MATCH_SUBURB_META = {
    exact: { text: 'text-emerald-700', bg: 'bg-emerald-50', ring: 'border-emerald-200' },
    surrounding: { text: 'text-sky-700', bg: 'bg-sky-50', ring: 'border-sky-200' },
    other: { text: 'text-gray-600', bg: 'bg-gray-50', ring: 'border-gray-200' },
};

// Which database a matched buyer came from.
const SOURCE_META = {
    portal: { label: 'Portal', cls: 'text-indigo-700 bg-indigo-100' },
    crm: { label: 'CRM', cls: 'text-violet-700 bg-violet-100' },
    database: { label: 'Database', cls: 'text-slate-700 bg-slate-200' },
};

// Open-home feedback chip (matches the buyer's interest_level bucket).
const FB_META = {
    hot: { label: 'Loved it', cls: 'text-orange-700 bg-orange-100' },
    warm: { label: 'Interested', cls: 'text-sky-700 bg-sky-100' },
    cold: { label: 'Lukewarm', cls: 'text-gray-500 bg-gray-100' },
    none: { label: 'Not keen', cls: 'text-gray-500 bg-gray-100' },
};
const fbBucket = (fb) => {
    const il = (fb?.interest_level || '').toLowerCase();
    if (['hot', 'very_high', 'high'].includes(il)) return 'hot';
    if (['warm', 'medium'].includes(il)) return 'warm';
    if (['cold', 'low'].includes(il)) return 'cold';
    if (il === 'none') return 'none';
    return fb?.text ? 'warm' : null;
};

// Stable per-result id (buyer_id is null for non-portal buyers, so it can't be the key).
const rowId = (b) => `${b.source}:${b.ref_id}`;

const scoreColor = (s) => {
    if (s >= 80) return 'text-emerald-600';
    if (s >= 60) return 'text-sky-600';
    if (s >= 40) return 'text-amber-600';
    return 'text-gray-500';
};

// A labelled min/max pair, e.g. Price [min][max].
const RangeField = ({ icon: Icon, label, minVal, maxVal, onMin, onMax, phMin = 'Min', phMax = 'Max', step }) => (
    <div className="text-xs text-gray-500">
        <span className="flex items-center gap-1 mb-1">{Icon && <Icon className="w-3 h-3" />} {label}</span>
        <div className="flex items-center gap-1.5">
            <input type="number" step={step} value={minVal} onChange={(e) => onMin(e.target.value)}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-gray-900" placeholder={phMin} />
            <span className="text-gray-300">to</span>
            <input type="number" step={step} value={maxVal} onChange={(e) => onMax(e.target.value)}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-gray-900" placeholder={phMax} />
        </div>
    </div>
);

const FitChip = ({ ok, near, label }) => {
    const cls = ok
        ? 'bg-emerald-50 text-emerald-700'
        : near
            ? 'bg-amber-50 text-amber-700'
            : 'bg-gray-100 text-gray-500';
    return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] ${cls}`}>{label}</span>;
};

// Strip a pref value like "$500,000" / "Any" / 6 to a plain number string for an input.
const prefNum = (v) => {
    if (v == null) return '';
    const s = String(v).replace(/[^0-9.]/g, '');
    return s || '';
};

// Edit a matched buyer's saved preferences. Writes back to their source database.
// Mirrors the buyer-portal Buying Preferences form: tick-box property types and a
// click-to-confirm suburb picker so agents can't mis-spell a suburb.
function EditBuyerModal({ buyer, onClose, onSaved }) {
    const [form, setForm] = useState(() => ({
        suburbs: buyer.pref_suburbs || [],
        priceMin: prefNum(buyer.pref_price_min), priceMax: prefNum(buyer.pref_price_max),
        bedsMin: prefNum(buyer.pref_bedrooms_min), bedsMax: prefNum(buyer.pref_bedrooms_max),
        bathsMin: prefNum(buyer.pref_bathrooms), bathsMax: prefNum(buyer.pref_bathrooms_max),
        carsMin: prefNum(buyer.pref_car_spaces), carsMax: prefNum(buyer.pref_car_spaces_max),
        types: buyer.pref_property_type || [],
    }));
    const [saving, setSaving] = useState(false);
    // Save also saves a status picked above the form (CRM-2026-00134).
    const statusRef = useRef(null);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const num = (v) => (v !== '' ? Number(v) : null);
    // Stable across form edits: BuyerStatusActions reloads whenever this object
    // changes, which would wipe an unsaved status pick on every keystroke.
    const statusIdentity = useMemo(() => ({
        contactId: buyer.contact_id || null,
        buyerId: buyer.buyer_id || null,
        email: buyer.email,
        name: buyer.name,
        phone: buyer.phone,
    }), [buyer.contact_id, buyer.buyer_id, buyer.email, buyer.name, buyer.phone]);

    const save = async () => {
        try {
            setSaving(true);
            if (statusRef.current && !(await statusRef.current.savePending())) return;
            await editBuyerPreferences({
                source: buyer.source, ref_id: buyer.ref_id,
                suburbs: form.suburbs,
                price_min: num(form.priceMin), price_max: num(form.priceMax),
                beds_min: num(form.bedsMin), beds_max: num(form.bedsMax),
                baths: num(form.bathsMin), baths_max: num(form.bathsMax),
                cars: num(form.carsMin), cars_max: num(form.carsMax),
                property_types: form.types,
            });
            toast.success('Preferences updated');
            onSaved && onSaved();
        } catch {
            toast.error('Could not save preferences');
        } finally {
            setSaving(false);
        }
    };

    const src = SOURCE_META[buyer.source] || SOURCE_META.portal;
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onMouseDown={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
                <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2 min-w-0">
                        <Pencil className="w-4 h-4 text-teal-600 shrink-0" />
                        <span className="text-sm font-semibold text-gray-900 truncate">Edit preferences · {buyer.name || buyer.email || 'Buyer'}</span>
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0 ${src.cls}`}>{src.label}</span>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto">
                {/* Buyer search status, the same four answers the buyer sees on
                    the portal (Christian Gravias, DOMA 13374913603). Saves on its
                    own button or with Save below; Purchased drops the buyer from
                    these results on the next search. An agent's No longer looking
                    is a note only and keeps them in. */}
                <BuyerStatusActions ref={statusRef} buyer={statusIdentity} />
                <div className="p-4 space-y-4">
                    <div>
                        <span className="block text-xs font-semibold text-gray-600 mb-1.5">Property types</span>
                        <PropertyTypeTiles value={form.types} onChange={(v) => set('types', v)} />
                    </div>
                    <div>
                        <span className="block text-xs font-semibold text-gray-600 mb-1.5">Suburbs</span>
                        <SuburbPicker value={form.suburbs} onChange={(v) => set('suburbs', v)} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <RangeField icon={Wallet} label="Price ($)" step="10000"
                            minVal={form.priceMin} maxVal={form.priceMax}
                            onMin={(v) => set('priceMin', v)} onMax={(v) => set('priceMax', v)} />
                        <RangeField icon={BedDouble} label="Bedrooms"
                            minVal={form.bedsMin} maxVal={form.bedsMax}
                            onMin={(v) => set('bedsMin', v)} onMax={(v) => set('bedsMax', v)} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <RangeField icon={Bath} label="Bathrooms"
                            minVal={form.bathsMin} maxVal={form.bathsMax}
                            onMin={(v) => set('bathsMin', v)} onMax={(v) => set('bathsMax', v)} />
                        <RangeField icon={Car} label="Car spaces"
                            minVal={form.carsMin} maxVal={form.carsMax}
                            onMin={(v) => set('carsMin', v)} onMax={(v) => set('carsMax', v)} />
                    </div>
                    {buyer.source === 'database' && (
                        <p className="text-[11px] text-amber-600">Imported database buyers store a single budget, min beds and one type — extra fields may not all be kept.</p>
                    )}
                </div>
                </div>
                <div className="flex-shrink-0 flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100">
                    <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                    <button onClick={save} disabled={saving}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
                    </button>
                </div>
            </div>
        </div>
    );
}

// Blast email / SMS to the matched buyers straight from the results list.
//
// This is the second composer in the CRM, and Christian wants the two to be one
// tool with two front doors (CG, 2026-08-28: "Please copy the same function you
// have designed already like the below in the communications tab" for email, and
// "We need to make the 2 text platforms conistent. They both have good perks"
// for text). So everything the Campaign Manager composer can do, this can do:
//
//   * Write with GeeVee - the same brief box and image/PDF drop zone, the same
//     endpoint (components/AiWritePanel, shared).
//   * Design the email full screen - the same drag-and-drop block builder, which
//     is what answers "We need to be able to move the image around" (CG,
//     2026-08-27).
//   * Templates, and Save as template, from the same library.
//   * On a text: pick the agent to call, place the unsubscribe link yourself,
//     and see the credit breakdown - the "perks" of the campaign SMS composer.
//   * The two tick boxes, from components/MessageExtras, on both channels.
//
// Everyone the search matched is reachable here, not a 200-buyer slice of them
// (CG, 2026-08-25). Because that can be a few thousand people, the backend
// answers straight away and sends in the background. Sending is gated
// server-side (consent + kill switch); we surface the outcome via toast.
const MAX_IMAGE_BYTES = 3 * 1024 * 1024; // 3 MB, hosted on S3 at send time

// Email composing has two modes, exactly like the Campaign editor:
//   quick   a plain message box - fastest way to fire off a note;
//   design  a full HTML email from GeeVee, a template or the block builder,
//           where the agent can move the image wherever they like.
// Generating with GeeVee, loading a template or opening the builder all switch
// to design; there is a way back, and the quick text is not thrown away.
const MODE_QUICK = 'quick';
const MODE_DESIGN = 'design';

// A stable id for one person in the recipient list, so the agent's "don't send
// to this one" ticks survive the parent re-rendering the list (the property
// tabs rebuild the array on every render). Falls back to the row's position
// only when there is nothing identifying on it at all.
export function recipientKey(r, index) {
    if (r?.source && r?.ref_id != null) return `s:${r.source}:${r.ref_id}`;
    if (r?.contact_id != null) return `c:${r.contact_id}`;
    if (r?.buyer_id != null) return `b:${r.buyer_id}`;
    const email = (r?.email || '').trim().toLowerCase();
    const phone = (r?.phone || '').trim();
    if (email || phone) return `x:${email}|${phone}`;
    return `i:${index}`;
}

// The question asked when the server says this exact blast already went out.
// A resend never reaches anyone twice: the server skips everyone who already
// got this exact message (Monday 13352813386, 29 Oct 2026).
export function repeatWarning(repeat, noun) {
    const when = formatMelbourneDateTime(repeat.sent_at);
    const total = new Intl.NumberFormat('en-AU').format(Number(repeat.total || 0));
    return `You already sent this ${noun} to ${total} people on ${when}.\n\n`
        + `Anyone who already received it will be skipped, so it only goes to `
        + `people on the list who have not had it yet.\n\n`
        + `Send it again anyway?`;
}

// How many rows of the recipient list are drawn at once. The Find Buyers blast
// can match a few thousand people and painting every row would stall the modal;
// the search box is how you reach the rest.
const RECIPIENT_ROWS_SHOWN = 200;

export function MessageMatchesModal({
    channel, recipients, propertyLabel, propertyId, currentUser, onClose, onSent,
    // What the recipients are called in the UI. Defaults to the Find Buyers
    // wording; the per-property Enquiries/Buyers tabs pass 'enquirer'/'buyer'.
    recipientNoun = 'matched buyer',
    // The "update preferences" invite needs a per-recipient saved-search link,
    // which arbitrary enquirers don't have — hidden when false.
    showPreferences = true,
    // Where the agent pressed send: 'enquirers' | 'attendees' | 'buyers' when
    // this is one of a listing's own tabs, blank for Find Buyers. Sent with the
    // blast so the Communications Hub files it under the listing instead of as
    // an anonymous Find Buyers send, and so the listing's Analytics tab can
    // find it again (Paul Jimoulis, CRM-2026-00072).
    sendContext = '',
}) {
    const isEmail = channel === 'email';
    const label = (propertyLabel || 'this property').trim();
    const nounPlural = `${recipientNoun}s`;
    const NounPlural = nounPlural.charAt(0).toUpperCase() + nounPlural.slice(1);
    // Passed in rather than read from the store here, so the composer stays a
    // plain presentational component the tests can render on its own.
    const user = currentUser || {};

    // Who is actually getting this. Everyone is in by default; the agent can
    // untick anyone they don't want it to reach (Michael Piccoli, Monday
    // 13056291936: "if there's someone I don't want it to go to there's no
    // option to untick them from receiving it"). Held as the set of people left
    // OUT, keyed by person, so a parent re-render can't quietly re-add someone.
    const [excluded, setExcluded] = useState(() => new Set());
    const [showRecipients, setShowRecipients] = useState(false);
    const [recipientSearch, setRecipientSearch] = useState('');

    const keyedRecipients = useMemo(
        () => recipients.map((r, i) => ({ ...r, _key: recipientKey(r, i) })),
        [recipients]
    );
    const selectedRecipients = useMemo(
        () => keyedRecipients.filter((r) => !excluded.has(r._key)),
        [keyedRecipients, excluded]
    );
    const removedCount = keyedRecipients.length - selectedRecipients.length;

    const toggleRecipient = (key) => setExcluded((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    const includeEveryone = () => setExcluded(new Set());
    const removeEveryone = () => setExcluded(new Set(keyedRecipients.map((r) => r._key)));

    // The list the panel draws: what the search box matches, capped so a few
    // thousand matches don't lock the browser up.
    const recipientQuery = recipientSearch.trim().toLowerCase();
    const visibleRecipients = useMemo(() => {
        if (!recipientQuery) return keyedRecipients.slice(0, RECIPIENT_ROWS_SHOWN);
        return keyedRecipients
            .filter((r) => `${r.name || ''} ${r.email || ''} ${r.phone || ''}`.toLowerCase().includes(recipientQuery))
            .slice(0, RECIPIENT_ROWS_SHOWN);
    }, [keyedRecipients, recipientQuery]);
    const matchedCount = recipientQuery
        ? keyedRecipients.filter((r) => `${r.name || ''} ${r.email || ''} ${r.phone || ''}`.toLowerCase().includes(recipientQuery)).length
        : keyedRecipients.length;

    const withEmail = selectedRecipients.filter((r) => r.email).length;
    const withPhone = selectedRecipients.filter((r) => r.phone).length;
    const reachable = isEmail ? withEmail : withPhone;
    const firstName = (selectedRecipients[0] && (selectedRecipients[0].name || '').split(' ')[0]) || '';

    const [subject, setSubject] = useState(
        isEmail ? `A property that matches your search: ${label}` : ''
    );
    // The inbox preview line - the short bit of text a mail client shows next to
    // the subject. The Campaign Manager has always had this field and Christian
    // wants the two composers to be the same tool (CG, 2026-09-01: "Can we
    // please add preview text"). Blank falls back to the subject at send time,
    // exactly as before.
    const [previewLine, setPreviewLine] = useState('');
    // The Preview text field auto-fills from the message body so the agent SEES
    // what the inbox will show (CG, Monday 12975097413: "preview text not auto
    // populating"). Tracks the last auto value so a hand-typed preview is never
    // overwritten - editing the message only refreshes a still-automatic one.
    const autoPreviewRef = useRef('');
    // The typed message is ONLY the agent's own words. The invitation and the
    // preferences line used to be baked in here as well as appended by their
    // tick box, which is why ticking the box changed nothing in the preview and
    // read as broken (CG, 2026-08-28: "There is a tick box button, how do they
    // get invited to the off market portal? in the preview text below its not
    // there even though the buttons ticked?"). Each block now has exactly one
    // owner, its tick box.
    const [body, setBody] = useState(() =>
        isEmail
            ? `Hi {{FirstName}},\n\nA property has just come up that matches what you're looking for: ${label}.\n\n`
              + `Would you like to arrange an inspection? Just reply to this email or give me a call.\n\n`
              + `Kind regards,\nCollings Real Estate`
            : `Hi {{FirstName}}, a property matching your search just came up: ${label}. Keen for a look? Reply or call us.`
    );
    // The message we opened with. Used only to tell an untouched default from
    // words the agent actually typed, before the property picker replaces them.
    const openingBody = useRef('');
    // Once, on mount: the default we generated. Deliberately not tracking `body`
    // after that - the point is to remember what it was BEFORE the agent (or the
    // property picker) touched it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { openingBody.current = body; }, []);
    const [image, setImage] = useState(null); // { name, dataUrl, size }
    // Locked on when this message was built FROM a property, the agent's call
    // otherwise (CG, Monday 13056948901, 2026-09-16: "Sorry dont make it
    // mandatory by default for emails & texts. Only make it mandatory if an
    // agent populates from a property"). A property page composer and Find
    // Buyers with a listing picked both have a propertyId; Find Buyers run off
    // hand-typed criteria does not, and there the tick boxes come back.
    //
    // Default when unlocked is on, on email where it costs nothing and, since
    // the shortener (CG, 2026-08-28: "reduce the characters used by using a link
    // shortener") took the preferences link from ~290 characters to 37, on a
    // text too. The tick box still prices whatever it does cost.
    const blocksLocked = Boolean(propertyId);
    const [offMarketChosen, setOffMarketChosen] = useState(true);
    const [preferencesChosen, setPreferencesChosen] = useState(showPreferences);
    const includeOffMarket = blocksLocked || offMarketChosen;
    // showPreferences is about whether a per-recipient link can be built at all,
    // not about agent choice. It overrides the lock, and addsPreferences below
    // is where that is applied.
    const includePreferences = blocksLocked || preferencesChosen;
    const [sending, setSending] = useState(false);
    // "Where does the image go?" / "If the button is ticked where does it go in
    // the email?" (CG, 2026-08-27) - both answered by showing the assembled
    // message rather than describing it. Open by default so nobody has to find it.
    const [showPreview, setShowPreview] = useState(true);
    // Starts on the agent's own mobile (or email) and can be changed to test on
    // someone else (Simon Abbott, Monday 13392775822). null = not edited yet.
    const [testToTyped, setTestToTyped] = useState(null);
    const [testing, setTesting] = useState(false);
    const fileRef = useRef(null);

    // "Populate from a property" - the same panel the Communications tab has
    // (CG, 2026-09-01). The page above already knows which address the agent
    // searched buyers for, but that address need not be a Collings listing, so
    // it cannot pull photos, price or a listing agent. This picks the real
    // record and fills the message from it.
    const [pickedProperty, setPickedProperty] = useState(null);
    const [picking, setPicking] = useState(false);
    // What the picker wrote, and what was there before, so Remove can put it
    // back instead of leaving a stale listing in the message.
    const appliedProperty = useRef(null);

    // Credits left on the SMS account, so an agent about to text a few thousand
    // buyers can see whether it will actually go (CG, 2026-09-01: "like an alert
    // if we run out of credits").
    const [smsBalance, setSmsBalance] = useState(null);
    const [smsBalanceLow, setSmsBalanceLow] = useState(false);

    // Email design mode.
    const [emailMode, setEmailMode] = useState(MODE_QUICK);
    const [html, setHtml] = useState('');       // full HTML document
    const [designJson, setDesignJson] = useState({});
    const [builderOpen, setBuilderOpen] = useState(false);
    // Bumped to force the (uncontrolled) builder to remount and re-seed when the
    // body is replaced wholesale (GeeVee write, start-from-template).
    const [builderKey, setBuilderKey] = useState(0);
    const [htmlMode, setHtmlMode] = useState('preview'); // preview | html

    // Visibly auto-populate the preview line from the message as it changes,
    // without ever clobbering one the agent typed themselves.
    useEffect(() => {
        if (!isEmail) return;
        const source = emailMode === MODE_QUICK ? body : html;
        const derived = derivePreviewFromBody(source);
        setPreviewLine((current) => {
            if (current && current !== autoPreviewRef.current) return current; // hand-typed
            autoPreviewRef.current = derived;
            return derived;
        });
    }, [isEmail, emailMode, body, html]);

    // Templates, from the same library the Campaign Manager uses.
    const [templates, setTemplates] = useState([]);
    const [showTemplates, setShowTemplates] = useState(false);
    const [savingTemplate, setSavingTemplate] = useState(false);
    // The {{merge tag}} vocabulary + the compose-scope values currently
    // resolvable (from this listing and the agent to contact). Same shared
    // helpers the Campaign Manager uses - see communications/utils/mergeTags.js.
    const [mergeVocab, setMergeVocab] = useState(null);
    const [resolvedTags, setResolvedTags] = useState({});
    // Why this listing has no buyer-portal page, when it has none. Find Buyers
    // is where CRM-2026-00081 happened: an appraisal record's {{PropertyLink}}
    // was filled in and texted, and every buyer who tapped it got
    // "Failed to load property details".
    const [linkProblem, setLinkProblem] = useState('');

    // SMS: who the recipient should call back. A broadcast text cannot be
    // replied to, so the campaign composer spells out an agent and appends their
    // number; the same block, the same helpers, so the two read identically.
    const [agents, setAgents] = useState([]);
    const [senderAgentId, setSenderAgentId] = useState('');
    // Text from COLLINGS instead of the agent's own mobile (CRM-2026-00138).
    const [smsFromCollings, setSmsFromCollings] = useState(false);
    const smsBodyRef = useRef(null);
    const agentBlockApplied = useRef(false);

    // Both endpoints answer in a different shape - communicationsApi unwraps
    // .data for you, the agent API hands back the raw axios response - and
    // either can page. Normalise to an array or the render blows up on .map.
    const asList = (r) => {
        const raw = r?.data ?? r;
        const list = raw?.results ?? raw;
        return Array.isArray(list) ? list : [];
    };

    useEffect(() => {
        fetchTemplates({ channel })
            .then((r) => setTemplates(asList(r)))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel]);

    useEffect(() => {
        fetchMergeTags().then(setMergeVocab).catch(() => {});
    }, []);

    // Find Buyers always has a listing, so the property tags ({{PropertyAddress}},
    // {{Price}}, {{AuctionDate}}, {{OpenTime}}) are resolvable here - which is
    // what makes the library's buyer templates usable straight from this screen.
    // A listing picked in the composer wins over the address the buyer search was
    // run on: it is a real record, so it can fill {{Price}}, {{AuctionDate}} and
    // the rest, and it is the one the agent just chose.
    const tagPropertyId = pickedProperty?.id || propertyId;
    useEffect(() => {
        if (!tagPropertyId && !senderAgentId) { setResolvedTags({}); return; }
        let cancelled = false;
        checkMergeTags({ text: '', agent_id: senderAgentId || null, property_id: tagPropertyId || null })
            .then((d) => {
                if (cancelled) return;
                setResolvedTags(d?.resolved || {});
                setLinkProblem(d?.property_link_problem || '');
            })
            .catch(() => { if (!cancelled) { setResolvedTags({}); setLinkProblem(''); } });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tagPropertyId, senderAgentId]);

    useEffect(() => {
        if (isEmail) return;
        let cancelled = false;
        fetchSmsCreditBalance()
            .then((res) => {
                if (cancelled) return;
                setSmsBalance(res?.credits ?? null);
                setSmsBalanceLow(!!res?.low);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [isEmail]);

    useEffect(() => {
        if (isEmail) return;
        getAgents({ page_size: 100 })
            .then((r) => setAgents(asList(r).filter((a) => a.email)))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEmail]);

    // Default the callback agent to whoever is logged in, and put their block on
    // the end of the message once.
    useEffect(() => {
        if (isEmail || !agents.length || agentBlockApplied.current) return;
        const mine = agents.find((a) => String(a.id) === String(user?.id))
            || agents.find((a) => (a.email || '').toLowerCase() === (user?.email || '').toLowerCase());
        if (!mine) return;
        agentBlockApplied.current = true;
        setSenderAgentId(String(mine.id));
        setBody((b) => stripSmsAgentBlock(b) + buildSmsAgentBlock(mine));
    }, [agents, isEmail, user]);

    const pickAgent = (id) => {
        setSenderAgentId(id);
        const agent = agents.find((a) => String(a.id) === String(id));
        setBody((b) => stripSmsAgentBlock(b) + (agent ? buildSmsAgentBlock(agent) : ''));
    };

    // Email is sent as branded HTML; keep the quick composer plain and convert
    // newlines. A designed email is already a document and goes as it is.
    const toHtml = (text) => text.replace(/\n/g, '<br>');
    const designing = isEmail && emailMode === MODE_DESIGN && !!html;

    // An agent is free to paste either merge tag into the message themselves. If
    // they have, the matching block is not appended a second time. The tick box
    // says so out loud instead of silently doing nothing, which is what made
    // this look broken.
    const composed = designing ? html : body;
    const offMarketInBody = composed.includes(OFF_MARKET_TAG);
    const preferencesInBody = composed.includes(PREFERENCES_TAG);
    const addsOffMarket = includeOffMarket && !offMarketInBody;
    const addsPreferences = includePreferences && showPreferences && !preferencesInBody;

    // What actually leaves: the message (typed or designed), the agent's image,
    // then the off market invitation, the preferences line and the unsubscribe
    // button - each on its own line, spaced apart (CG, 2026-08-28: "Please space
    // out the sentences. Its all to cludded together").
    const emailHtml = () => {
        const imageHtml = image
            ? `<p style="margin:16px 0"><img src="${image.dataUrl}" alt="" `
              + 'style="max-width:100%;height:auto;border-radius:8px" /></p>'
            : '';
        const opts = { offMarket: addsOffMarket, preferences: addsPreferences, unsubscribe: true };
        if (designing) {
            // A designed email carries its own images; the quick picker is
            // hidden in that mode, so there is nothing to splice in here.
            return appendEmailBlocks(html, opts);
        }
        return toHtml(body) + imageHtml + emailBlocksHtml(opts);
    };

    const smsText = () => assembleSms(body, { offMarket: addsOffMarket, preferences: addsPreferences });

    // A blast goes out as whoever is logged in, from their own mobile once it is
    // an active sender, and then has to open with their intro line (Monday
    // 13392775822). The send adds it; this is so the agent sees it and the
    // credits count it. smsText() stays without it - that is what gets posted.
    const agentSmsSender = useSmsSender(null, !isEmail);
    const smsSender = smsFromCollings ? COLLINGS_SENDER : agentSmsSender;
    const smsAsSent = () => withSmsIntro(smsText(), smsSender.intro);

    // Per-message credits, times the number of buyers being texted. Priced on
    // the message as it will really be sent: merge tags expanded to the URLs
    // they become, those URLs shortened, then link tracking and the opt-out
    // clause.
    const creditsFor = (text) => smsInfo(asSent(pricedText(withSmsIntro(text, smsSender.intro)))).credits;
    const sms = smsInfo(asSent(pricedText(smsAsSent())));
    const totalCredits = sms.credits * reachable;

    // What the preferences line costs on a text, priced exactly the way the send
    // is. Usually nothing now the link is shortened, but a message already near
    // a segment boundary can still tip over, and the agent should see that
    // before they send it to a few thousand people - whether they ticked it or
    // the property lock put it there.
    const withoutPreferences = assembleSms(body, { offMarket: addsOffMarket });
    const preferencesExtraCredits = isEmail ? 0 : Math.max(
        0,
        creditsFor(assembleSms(body, { offMarket: addsOffMarket, preferences: true }))
            - creditsFor(withoutPreferences),
    );

    const pickImage = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ''; // let the same file be picked again after a remove
        if (!file) return;
        if (!/^image\//.test(file.type)) { toast.error('Pick an image file.'); return; }
        if (file.size > MAX_IMAGE_BYTES) { toast.error('Images need to be under 3MB.'); return; }
        const reader = new FileReader();
        reader.onload = () => setImage({ name: file.name, dataUrl: String(reader.result), size: file.size });
        reader.onerror = () => toast.error('Could not read that image.');
        reader.readAsDataURL(file);
    };

    // GeeVee's draft. On email it becomes a designed document (subject and all),
    // which is the point - the agent can then open the builder and move things
    // around. On a text it replaces the words and keeps the agent's sign-off.
    //
    // GeeVee writes the preview line as well as the subject (campaign_writer
    // returns preview_text), so fill that field too - it was left blank here
    // while the Campaign Manager filled it, and the inbox then fell back to
    // showing the first words of the email (PJ, 2026-09-14).
    const applyAiResult = (res) => {
        if (isEmail) {
            if (res.subject) setSubject(res.subject);
            if (res.preview_text) setPreviewLine(res.preview_text);
            setHtml(res.body_html || '');
            setDesignJson({});
            setEmailMode(MODE_DESIGN);
            setHtmlMode('preview');
            setBuilderKey((k) => k + 1);
        } else {
            const agent = agents.find((a) => String(a.id) === String(senderAgentId));
            setBody(stripSmsAgentBlock(res.body || '') + (agent ? buildSmsAgentBlock(agent) : ''));
        }
    };

    // Fill the composer from a real listing. Email becomes the designed property
    // email (photos, features, price, the agent's signature); a text becomes the
    // Just Listed message. Both are the same builders the Campaign Manager uses,
    // so the two composers produce the same message from the same listing.
    const applyProperty = async (row) => {
        setPicking(true);
        try {
            const data = await fetchPropertyEmailData(row.id);
            const meta = propertyEmailMeta(data);
            const propertyHtml = buildPropertyEmailHtml(data, { intro: propertyEmailIntro(data) });
            const smsBody = buildPropertyJustListedSms(data);

            // An untouched default is ours to replace. Anything the agent typed
            // is theirs, so ask first rather than binning it.
            const typed = isEmail
                ? (designing ? html : body)
                : stripSmsAgentBlock(body);
            const untouched = isEmail
                ? (designing ? !html.trim() : body === openingBody.current)
                : typed === stripSmsAgentBlock(openingBody.current);
            if (!untouched && !window.confirm(
                `Replace your message with the Just Listed content for ${data.address}?

`
                + 'Cancel keeps what you have written and only remembers the property '
                + 'for merge tags.'
            )) {
                setPickedProperty(data);
                return;
            }

            appliedProperty.current = {
                html: propertyHtml,
                sms: smsBody,
                preview: previewLine || meta.preview_text,
                prevHtml: html,
                prevBody: body,
                prevPreview: previewLine,
                prevMode: emailMode,
            };
            setPickedProperty(data);
            if (isEmail) {
                setPreviewLine((p) => p || meta.preview_text);
                setHtml(propertyHtml);
                setDesignJson({});
                setEmailMode(MODE_DESIGN);
                setHtmlMode('preview');
                setBuilderKey((k) => k + 1);
            } else {
                const agent = agents.find((a) => String(a.id) === String(senderAgentId));
                setBody(smsBody + (agent ? buildSmsAgentBlock(agent) : ''));
            }
            // Text from the listing agent where we can, the same as the campaign
            // composer does - they are the one the buyer will call back.
            if (!isEmail && data.agent?.id) {
                const match = agents.find((a) => String(a.id) === String(data.agent.id));
                if (match) pickAgent(String(match.id));
            }
            toast.success(`Loaded ${data.address}`);
        } catch {
            toast.error("Couldn't load that property");
        } finally {
            setPicking(false);
        }
    };

    const clearProperty = () => {
        const applied = appliedProperty.current;
        if (applied) {
            // Only put the old message back while what is on screen is still the
            // one WE wrote - otherwise the agent has edited it since and it is
            // theirs to keep.
            if (isEmail && html === applied.html) {
                setHtml(applied.prevHtml || '');
                setDesignJson({});
                setEmailMode(applied.prevMode);
                setBuilderKey((k) => k + 1);
            }
            if (!isEmail && stripSmsAgentBlock(body) === applied.sms) {
                const agent = agents.find((a) => String(a.id) === String(senderAgentId));
                setBody(applied.prevBody || (agent ? buildSmsAgentBlock(agent) : ''));
            }
            if (previewLine === applied.preview) setPreviewLine(applied.prevPreview || '');
            appliedProperty.current = null;
        }
        setPickedProperty(null);
    };

    const applyTemplate = (tpl) => {
        // Fill the compose-scope tags now, from this listing and the selected
        // agent, so the agent sees the real address/price/auction time in the box
        // instead of it arriving as a blank gap at send time. Per-recipient tags
        // ({{FirstName}}) are left for the send engine (Monday 12918907065).
        const fill = (text) => substituteTags(text, resolvedTags, mergeVocab);
        if (isEmail) {
            if (tpl.subject) setSubject(fill(tpl.subject));
            setHtml(fill(tpl.html_body || ''));
            setDesignJson(tpl.design_json || {});
            setEmailMode(MODE_DESIGN);
            setHtmlMode('preview');
            setBuilderKey((k) => k + 1);
        } else {
            const agent = agents.find((a) => String(a.id) === String(senderAgentId));
            setBody(fill(stripSmsAgentBlock(tpl.sms_body || '')) + (agent ? buildSmsAgentBlock(agent) : ''));
        }
        setShowTemplates(false);
        const missing = (tpl.required_tags || []).filter((n) => !resolvedTags[n]);
        if (missing.length) {
            toast(`Loaded "${tpl.name}" - still needs ${missing.join(', ')}. Edit the message before sending.`,
                { icon: '⚠️', duration: 6000 });
        } else {
            toast.success(`Loaded "${tpl.name}"`);
        }
    };

    const saveAsTemplate = async () => {
        const name = window.prompt('Name this template', label || 'New template');
        if (!name) return;
        setSavingTemplate(true);
        try {
            const payload = isEmail
                ? {
                    name, channel: 'email', category: 'general',
                    subject,
                    html_body: designing ? html : toHtml(body),
                    design_json: designJson,
                }
                : {
                    name, channel: 'sms', category: 'general',
                    // Without the agent-specific block; it is re-appended from
                    // the picker when the template is used.
                    sms_body: stripSmsAgentBlock(body),
                };
            const tpl = await createTemplate(payload);
            setTemplates((prev) => [tpl, ...prev]);
            toast.success('Saved as template');
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save template");
        } finally {
            setSavingTemplate(false);
        }
    };

    const openBuilder = () => {
        // Seed the canvas from whatever is written so far, so opening the
        // designer never loses the quick message the agent already typed.
        if (!html && body.trim()) setHtml(toHtml(body));
        setEmailMode(MODE_DESIGN);
        setBuilderOpen(true);
    };

    // EmailBuilder emits { html_body, design_json }. This used to destructure
    // { html, design }, which never matched, so EVERY edit made in the
    // full-screen designer was silently thrown away - including the link an
    // agent put on a button (Paul Jimoulis, CRM-2026-00047).
    const handleBuilderChange = ({ html_body, design_json, html: legacyHtml, design: legacyDesign }) => {
        const nextHtml = typeof html_body === 'string' ? html_body : legacyHtml;
        const nextDesign = design_json || legacyDesign;
        if (typeof nextHtml === 'string') setHtml(nextHtml);
        if (nextDesign) setDesignJson(nextDesign);
    };

    const hasMessage = () => (designing ? !!html.trim() : !!body.trim());

    // One copy of the real thing to an address/number the agent types in, before
    // they commit to a few thousand sends (CG, 2026-08-27: "I would like to see
    // how the sms/email comes out"). It carries the same image, the same Off
    // Market Portal invitation and the same unsubscribe button, and it borrows a
    // matched buyer's details purely so the merge tags render real values.
    const myTestAddress = isEmail
        ? (user.email || '')
        : ((agents.find((a) => String(a.id) === String(user.id))
            || agents.find((a) => (a.email || '').toLowerCase() === (user.email || '').toLowerCase())
            || {}).phone_number || user.phone_number || '');
    const testTo = testToTyped ?? myTestAddress;

    const sendTest = async () => {
        const to = testTo.trim();
        if (!to) { toast.error(isEmail ? 'Enter an email address to test with.' : 'Enter a mobile number to test with.'); return; }
        if (isEmail && !to.includes('@')) { toast.error('That does not look like an email address.'); return; }
        if (!hasMessage()) { toast.error('Add a message first.'); return; }
        if (isEmail && !subject.trim()) { toast.error('Add a subject first.'); return; }
        const sample = selectedRecipients[0] || recipients[0] || {};
        try {
            setTesting(true);
            const res = await sendTestBuyerMatchMessage({
                channel,
                subject: isEmail ? subject.trim() : '',
                preview_text: isEmail ? previewLine.trim() : '',
                body: isEmail ? emailHtml() : smsText(),
                to,
                sample: { source: sample.source, ref_id: sample.ref_id, name: sample.name },
                ...(isEmail ? {} : { sms_from_collings: smsFromCollings }),
            });
            if (res.sent) {
                toast.success(`Test ${isEmail ? 'email' : 'text'} sent to ${to}.`);
            } else {
                toast.error(res.error || 'The test message did not go through.');
            }
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Could not send the test message.');
        } finally {
            setTesting(false);
        }
    };

    const send = async () => {
        if (!hasMessage()) { toast.error('Add a message first.'); return; }
        if (isEmail && !subject.trim()) { toast.error('Add a subject first.'); return; }
        if (selectedRecipients.length === 0) {
            toast.error(`Tick at least one ${recipientNoun} to send to.`);
            return;
        }
        if (reachable === 0) {
            toast.error(isEmail ? `None of these ${nounPlural} have an email.` : `None of these ${nounPlural} have a phone number.`);
            return;
        }
        // Merge-tag guard: the send engine blanks any tag it cannot fill, so an
        // unresolved {{AuctionDate}} would go out to every matched buyer as a
        // gap. Ask before that reaches the whole list (Monday 12918907065).
        const draftText = `${isEmail ? subject : ''}\n${body}`;
        const gaps = unresolvedTags(mergeVocab, draftText, resolvedTags,
            linkProblem ? { PropertyLink: linkProblem } : {});
        if (gaps.length && !window.confirm(
            'This message has merge tags with nothing to fill them in.\n\n'
            + gaps.map((g) => `  {{${g.name}}} - ${g.reason}`).join('\n')
            + `\n\n${unresolvedSummary(gaps)}\n\nSend anyway?`
        )) return;
        try {
            setSending(true);
            const payload = {
                channel,
                subject: isEmail ? subject.trim() : '',
                preview_text: isEmail ? previewLine.trim() : '',
                body: isEmail ? emailHtml() : smsText(),
                // Where this went out from. `about` is the address as it reads
                // today, so the send still names its listing after the listing
                // is gone; `property_id` is the join the property page uses.
                ...(sendContext ? {
                    context: sendContext,
                    property_id: propertyId || null,
                    about: (propertyLabel || '').trim(),
                } : {}),
                ...(isEmail ? {} : { sms_from_collings: smsFromCollings }),
                // Only the people still ticked. Anyone the agent removed never
                // reaches the server, so no worker can send to them.
                recipients: selectedRecipients.map((r) => ({
                    source: r.source, ref_id: r.ref_id,
                    buyer_id: r.buyer_id, contact_id: r.contact_id,
                    email: r.email, phone: r.phone, name: r.name,
                })),
            };
            const noun = isEmail ? 'email' : 'text';
            let res;
            try {
                res = await messageBuyerMatches(payload);
            } catch (err) {
                // The server holds back the same blast a second time (Monday
                // 13352813386: one email went to 838 buyers three times after
                // repeat presses). Say when it went and only resend on a yes.
                const repeat = err?.response?.status === 409 && err.response.data?.repeat;
                if (!repeat) throw err;
                if (!window.confirm(repeatWarning(repeat, noun))) return;
                res = await messageBuyerMatches({ ...payload, force: true });
            }
            if (res.simulated) {
                toast.success(
                    `Simulated ${res.reachable ?? reachable} ${noun}s, sending is currently switched off (nothing left the building).`,
                    { duration: 6000 }
                );
            } else if (res.queued) {
                // The blast runs on a worker: thousands of sends can take a few
                // minutes, so say so rather than pretending it is already done.
                toast.success(
                    `Sending ${(res.reachable ?? reachable).toLocaleString()} ${noun}s in the background. `
                    + 'Large lists can take a few minutes.',
                    { duration: 6000 }
                );
            } else {
                const sent = res.sent || 0;
                const skipped = res.skipped || 0;
                toast.success(`Sent ${sent} ${noun}${sent !== 1 ? 's' : ''}${skipped ? ` · ${skipped} skipped (opted out, no address or already received it)` : ''}.`);
            }
            onSent && onSent();
        } catch {
            toast.error('Could not send the messages.');
        } finally {
            setSending(false);
        }
    };

    const toolbarButton = 'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-gray-600 hover:text-gray-800 border border-gray-200 disabled:opacity-50';

    return (
        // No background-click close. This composer holds a typed message, a
        // subject and a whole email design, and a stray click on the dark
        // surround binned the lot with no undo - the same call Christian made
        // on the lead note modals. The X and Cancel close it.
        <>
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
                <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2 min-w-0">
                        {isEmail ? <Mail className="w-4 h-4 text-indigo-600 shrink-0" /> : <MessageSquare className="w-4 h-4 text-indigo-600 shrink-0" />}
                        <span className="text-sm font-semibold text-gray-900 truncate">
                            {isEmail ? 'Email' : 'Text'} {selectedRecipients.length.toLocaleString()} {selectedRecipients.length !== 1 ? nounPlural : recipientNoun}
                        </span>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    <p className="text-xs text-gray-500">
                        {reachable.toLocaleString()} of {selectedRecipients.length.toLocaleString()} {reachable === 1 ? 'has' : 'have'} {isEmail ? 'an email address' : 'a phone number'} and will be contacted.
                        {reachable !== selectedRecipients.length && ' The rest are skipped.'} {NounPlural} who opted out are always skipped.
                        {removedCount > 0 && ` You have taken ${removedCount.toLocaleString()} off this send.`}
                    </p>

                    {/* Who it goes to. Everyone starts ticked; untick anyone who
                        should not get this one (Michael Piccoli, Monday
                        13056291936). The panel is collapsed so the composer
                        opens the way it always has. */}
                    <div className="border border-gray-200 rounded-lg">
                        <button type="button" onClick={() => setShowRecipients((v) => !v)}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left">
                            <span className="flex items-center gap-2 text-xs font-bold text-gray-700">
                                <Users className="w-3.5 h-3.5 text-indigo-600" />
                                Recipients
                                <span className="font-semibold text-gray-500">
                                    {selectedRecipients.length.toLocaleString()} of {keyedRecipients.length.toLocaleString()} selected
                                </span>
                            </span>
                            {showRecipients ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </button>
                        {showRecipients && (
                            <div className="border-t border-gray-100 p-3 space-y-2">
                                <div className="flex items-center gap-2">
                                    <div className="relative flex-1">
                                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                                        <input
                                            value={recipientSearch}
                                            onChange={(e) => setRecipientSearch(e.target.value)}
                                            placeholder={`Search ${nounPlural} by name, email or number`}
                                            className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        />
                                    </div>
                                    <button type="button" onClick={includeEveryone} className={toolbarButton}>Select all</button>
                                    <button type="button" onClick={removeEveryone} className={toolbarButton}>Clear all</button>
                                </div>
                                <div className="max-h-56 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-md">
                                    {visibleRecipients.length === 0 && (
                                        <p className="px-3 py-3 text-xs text-gray-500">No {nounPlural} match that search.</p>
                                    )}
                                    {visibleRecipients.map((r) => {
                                        const contactable = isEmail ? !!r.email : !!r.phone;
                                        const ticked = !excluded.has(r._key);
                                        return (
                                            <label key={r._key}
                                                className={`flex items-center gap-2 px-3 py-2 text-xs ${contactable ? 'cursor-pointer hover:bg-gray-50' : 'bg-gray-50'}`}>
                                                <input type="checkbox" checked={ticked && contactable} disabled={!contactable}
                                                    onChange={() => toggleRecipient(r._key)}
                                                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-40" />
                                                <span className="min-w-0 flex-1">
                                                    <span className="block font-semibold text-gray-800 truncate">{r.name || 'Unnamed'}</span>
                                                    <span className="block text-gray-500 truncate">
                                                        {contactable
                                                            ? (isEmail ? r.email : r.phone)
                                                            : (isEmail ? 'No email address - will be skipped' : 'No phone number - will be skipped')}
                                                    </span>
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                                {matchedCount > visibleRecipients.length && (
                                    <p className="text-[11px] text-gray-500">
                                        Showing the first {visibleRecipients.length.toLocaleString()} of {matchedCount.toLocaleString()}. Search to find anyone else.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* The same "Populate from a property" search as the
                        Communications tab, and the same component (CG,
                        2026-09-01: "we need to add the search a property section
                        which will pull all the info of the property"). */}
                    <PropertyPickerPanel
                        selected={pickedProperty}
                        onPick={applyProperty}
                        onClear={clearProperty}
                        busy={picking}
                        blurb={isEmail
                            ? <>Pick the listing and we'll drop in its address, photos, details, price and the listing agent - ideal for a <strong>Just Listed</strong> email. You can keep editing after.</>
                            : <>Pick the listing and we'll write the <strong>Just Listed</strong> text from it - address, features, price and the link - and text from the listing agent. You can keep editing after.</>}
                    />

                    {/* The same "Write with GeeVee" box as the Communications tab
                        (CG, 2026-08-28), same component, same endpoint. */}
                    <AiWritePanel
                        channel={channel}
                        onWritten={applyAiResult}
                        audienceHint={`buyers whose saved search matches ${label}`}
                        supportEmail={user?.email || ''}
                        propertyId={tagPropertyId}
                        compact
                        placeholder={isEmail
                            ? `e.g. "Write an email to buyers about ${label}, inviting them to the open home"`
                            : `e.g. "Short text telling buyers ${label} just came up"`}
                    />

                    {/* SMS: who the recipient calls back. A broadcast text can't
                        be replied to, so the agent is named in the message -
                        same block the campaign composer appends. */}
                    {!isEmail && (
                        <div className="rounded-lg border border-gray-200 p-3 space-y-1.5">
                            <div className="flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5 text-indigo-600" />
                                <span className="text-xs font-semibold text-gray-700">Agent to contact</span>
                            </div>
                            <select
                                value={senderAgentId}
                                onChange={(e) => pickAgent(e.target.value)}
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
                            >
                                <option value="">No agent block</option>
                                {agents.map((a) => (
                                    <option key={a.id} value={a.id}>{a.name || a.email}</option>
                                ))}
                            </select>
                            <p className="text-[11px] text-gray-400">
                                Added to the end of the text so recipients know who to call.
                            </p>
                            <SmsSendFrom
                                className="pt-1"
                                agentSender={agentSmsSender}
                                fromCollings={smsFromCollings}
                                onChange={setSmsFromCollings}
                            />
                        </div>
                    )}

                    {isEmail && (
                        <label className="block">
                            <span className="block text-xs font-semibold text-gray-600 mb-1">Subject</span>
                            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400" />
                        </label>
                    )}

                    {/* Preview text: the line the inbox shows next to the
                        subject. Same field, same behaviour as the Campaign
                        Manager (CG, 2026-09-01). */}
                    {isEmail && (
                        <label className="block">
                            <span className="block text-xs font-semibold text-gray-600 mb-1">Preview text</span>
                            <input type="text" value={previewLine} onChange={(e) => setPreviewLine(e.target.value)}
                                aria-label="Preview text"
                                placeholder="Shown next to the subject in the inbox - leave blank to reuse the subject"
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400" />
                        </label>
                    )}

                    <div>
                        <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                            <span className="text-xs font-semibold text-gray-600">
                                {isEmail ? 'Message' : 'Message'} - use {'{{FirstName}}'}, {'{{Suburb}}'}
                                {!isEmail && ` (${smsAsSent().length} chars · ${sms.credits} credit${sms.credits === 1 ? '' : 's'} each)`}
                            </span>
                            <div className="flex items-center gap-2 flex-wrap">
                                {!isEmail && (
                                    /* Puts the opt-out link at the end of the text, whatever the
                                       cursor is on, and the send keeps it below the portal and
                                       preferences lines (Simon Abbott, Monday 13392775822: "when
                                       the agent clicks "insert unsubscribe link" it automatically
                                       puts it at the end of the text message"). */
                                    <button type="button" className={toolbarButton}
                                        onClick={() => {
                                            if (body.includes(SMS_STOP_PLACEHOLDER)) {
                                                toast.error('This message already has an unsubscribe link placeholder.');
                                                return;
                                            }
                                            setBody(appendStopPlaceholder(body));
                                        }}>
                                        <Link2 className="w-3.5 h-3.5" /> Insert unsubscribe link
                                    </button>
                                )}
                                <button type="button" className={toolbarButton} onClick={() => setShowTemplates(true)}>
                                    <LayoutTemplate className="w-3.5 h-3.5" /> Templates
                                </button>
                                <button type="button" className={toolbarButton} onClick={saveAsTemplate}
                                    disabled={savingTemplate || !hasMessage()}>
                                    <Save className="w-3.5 h-3.5" /> {savingTemplate ? 'Saving…' : 'Save as template'}
                                </button>
                                {designing && (
                                    <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
                                        {[
                                            { key: 'preview', label: 'Preview', Icon: Eye },
                                            { key: 'html', label: 'HTML', Icon: Code },
                                        ].map(({ key, label: lbl, Icon }) => (
                                            <button key={key} type="button" onClick={() => setHtmlMode(key)}
                                                className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold ${htmlMode === key ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500'}`}>
                                                <Icon className="w-3.5 h-3.5" /> {lbl}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {isEmail && (
                            /* The drag-and-drop designer, the same one the
                               Communications tab opens - this is what lets an
                               agent move the image around (CG, 2026-08-27). */
                            <button type="button" onClick={openBuilder}
                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 mb-2">
                                <Blocks className="w-4 h-4" /> {html ? 'Edit design (full screen)' : 'Design email (full screen)'}
                            </button>
                        )}

                        {designing ? (
                            htmlMode === 'preview' ? (
                                <iframe title="Email preview" sandbox="" srcDoc={previewHtml(emailHtml(), firstName)}
                                    className="w-full rounded-lg border border-gray-200 bg-white" style={{ height: 380 }} />
                            ) : (
                                <textarea value={html} onChange={(e) => { setHtml(e.target.value); setDesignJson({}); }} rows={14}
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs font-mono focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400" />
                            )
                        ) : (
                            <>
                                {!isEmail && <SmsIntroHead intro={smsSender.intro} sender={smsSender.sender} />}
                                <textarea ref={smsBodyRef} aria-label="Message" value={body} onChange={(e) => setBody(e.target.value)} rows={isEmail ? 8 : 5}
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400" />
                            </>
                        )}

                        {/* This listing has no page on the buyer portal, so a
                            property link would open an error for the buyer. Said
                            here, while it can still be fixed, rather than after
                            the whole match list has tapped it (CRM-2026-00081). */}
                        {linkProblem ? (
                            <p className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2 text-[11px] font-semibold text-amber-800">
                                {linkProblem}
                            </p>
                        ) : null}

                        {/* Attached to the bottom of the box, because the message
                            box is where Christian looked for these lines and did
                            not find them (CG, 2026-09-01: "I have ticked the
                            invite them to off market portal & update preference
                            buttons but I cant see it added in the text
                            message"). They were always being sent - the credit
                            count above already included them - they were just
                            only visible in the preview further down. */}
                        <MessageBlocksTail
                            channel={channel}
                            offMarket={addsOffMarket}
                            preferences={addsPreferences}
                            unsubscribePlaced={!isEmail && body.includes(SMS_STOP_PLACEHOLDER)}
                        />

                        {designing && (
                            <button type="button" onClick={() => setEmailMode(MODE_QUICK)}
                                className="mt-1 text-[11px] text-indigo-600 hover:underline">
                                Back to the quick message box
                            </button>
                        )}
                        {!isEmail && body.trim() && (
                            <p className="mt-1 text-[11px] text-gray-500">
                                This message sends as{' '}
                                <span className={sms.credits > 1 ? 'font-semibold text-amber-600' : 'font-semibold text-gray-700'}>
                                    {sms.credits} credit{sms.credits === 1 ? '' : 's'}
                                </span>{' '}
                                per recipient
                                {sms.credits > 1
                                    ? ` (over ${sms.encoding === 'Unicode' ? 70 : 160} characters, so it splits into ${sms.credits} texts)`
                                    : '.'}
                                {sms.encoding === 'Unicode' && ' Emoji/special characters make this a Unicode message (70 chars per credit).'}
                                {' '}
                                Ends with an "Unsubscribe" link, always the last line.
                            </p>
                        )}
                    </div>

                    {/* Quick email extras: an image the agent picks. Hidden once
                        they are designing, where images live in the builder and
                        can be put anywhere. */}
                    {isEmail && !designing && (
                        <div className="rounded-lg border border-gray-200 p-3 space-y-2.5">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-semibold text-gray-600">Image</span>
                                <button type="button" onClick={() => fileRef.current && fileRef.current.click()}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-indigo-200 text-indigo-700 text-xs font-semibold hover:bg-indigo-50">
                                    <ImagePlus className="w-3.5 h-3.5" /> {image ? 'Replace image' : 'Add an image'}
                                </button>
                                <input ref={fileRef} type="file" accept="image/*" onChange={pickImage} className="hidden" />
                            </div>
                            {image ? (
                                <div className="flex items-center gap-2">
                                    <img src={image.dataUrl} alt={image.name}
                                        className="w-16 h-16 object-cover rounded-md border border-gray-200" />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-xs text-gray-700 truncate">{image.name}</div>
                                        <div className="text-[11px] text-gray-400">{Math.round(image.size / 1024)} KB, sits under your message</div>
                                    </div>
                                    <button type="button" onClick={() => setImage(null)}
                                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" title="Remove image">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <p className="text-[11px] text-gray-400">
                                    A photo of the property, a floorplan, anything up to 3MB. Design the email instead if you want to move it around.
                                </p>
                            )}
                        </div>
                    )}

                    <MessageExtras
                        channel={channel}
                        offMarket={includeOffMarket}
                        onOffMarketChange={setOffMarketChosen}
                        preferences={includePreferences}
                        onPreferencesChange={setPreferencesChosen}
                        offMarketInBody={offMarketInBody}
                        preferencesInBody={preferencesInBody}
                        preferencesExtraCredits={preferencesExtraCredits}
                        recipientCount={reachable}
                        showPreferences={showPreferences}
                        locked={blocksLocked}
                    />

                    {/* What actually leaves the building, assembled in order.
                        Christian asked where the image lands and where the
                        ticked invitation lands (2026-08-27); showing it beats
                        explaining it. */}
                    <div className="rounded-lg border border-gray-200">
                        <button type="button" onClick={() => setShowPreview((v) => !v)}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left">
                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                                {showPreview ? <Eye className="w-3.5 h-3.5 text-indigo-600" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
                                Preview {isEmail ? 'the email' : 'the text'}
                            </span>
                            {showPreview ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </button>
                        {showPreview && (
                            <div className="px-3 pb-3 space-y-2">
                                <p className="text-[11px] text-gray-400">
                                    Exactly what each {recipientNoun} receives, in this order. Merge tags show sample values here
                                    and are filled in per recipient when it sends.
                                </p>
                                {isEmail ? (
                                    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                                        <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
                                            <div className="text-[10px] uppercase tracking-wide text-gray-400">Subject</div>
                                            <div className="text-xs font-semibold text-gray-800 break-words">
                                                {previewText(subject, firstName) || <span className="text-gray-400">No subject yet</span>}
                                            </div>
                                            {/* The inbox shows the preview line right after the
                                                subject, so the preview does too. */}
                                            <div className="text-[11px] text-gray-500 break-words mt-0.5">
                                                {previewText(previewLine, firstName)
                                                    || <span className="text-gray-400">Preview text falls back to the subject</span>}
                                            </div>
                                        </div>
                                        {designing ? (
                                            <iframe title="Assembled email" sandbox="" srcDoc={previewHtml(emailHtml(), firstName)}
                                                className="w-full bg-white" style={{ height: 320 }} />
                                        ) : (
                                            <div className="p-3 max-h-72 overflow-y-auto text-sm text-gray-700 [&_a]:text-indigo-600 [&_a]:underline [&_img]:max-w-full [&_img]:rounded-lg"
                                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewHtml(emailHtml(), firstName)) }} />
                                        )}
                                    </div>
                                ) : (
                                    <div className="rounded-2xl bg-gray-100 px-3 py-2.5 text-sm text-gray-800 whitespace-pre-wrap break-words max-h-60 overflow-y-auto">
                                        {previewText(asSent(smsAsSent()), firstName)}
                                    </div>
                                )}
                                <p className="text-[11px] text-gray-400">
                                    {isEmail
                                        ? `Your message, then your image${addsOffMarket ? ', then the Off Market Portal invitation' : ''}`
                                          + `${addsPreferences ? ', then the preferences link' : ''}, then the Unsubscribe button. `
                                          + 'Collings branding is added around it when it sends.'
                                        : `${smsSender.intro ? 'Your intro line, then your' : 'Your'} message${addsOffMarket ? ', then the Off Market Portal invitation' : ''}`
                                          + `${addsPreferences ? ', then the preferences link' : ''}, then the tap to stop opt-out link, `
                                          + 'each on its own line. Long links are shortened when it sends.'}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Send yourself one copy first (CG, 2026-08-27). Delivers
                        even while the global send switch is off, because a
                        preview that only simulates proves nothing. */}
                    <div className="rounded-lg border border-gray-200 p-3 space-y-2">
                        <div className="flex items-center gap-1.5">
                            <FlaskConical className="w-3.5 h-3.5 text-indigo-600" />
                            <span className="text-xs font-semibold text-gray-700">
                                Send a test {isEmail ? 'email' : 'text'} first
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type={isEmail ? 'email' : 'tel'}
                                value={testTo}
                                onChange={(e) => setTestToTyped(e.target.value)}
                                placeholder={isEmail ? 'you@collings.com.au' : '04XX XXX XXX'}
                                className="flex-1 min-w-0 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400" />
                            <button type="button" onClick={sendTest} disabled={testing}
                                className="inline-flex shrink-0 items-center gap-1.5 px-3 py-2 rounded-lg border border-indigo-200 text-indigo-700 text-xs font-semibold hover:bg-indigo-50 disabled:opacity-50">
                                {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                Send test
                            </button>
                        </div>
                        <p className="text-[11px] text-gray-400">
                            Goes only to this {isEmail ? 'address' : 'number'}, never to the {nounPlural}, and arrives marked as a test.
                        </p>
                    </div>

                    {!isEmail && reachable > 0 && (
                        <p className="text-[11px] text-gray-500">
                            {sms.credits} credit{sms.credits !== 1 ? 's' : ''} per text ({sms.encoding}), about{' '}
                            <span className="font-semibold text-gray-700">{totalCredits.toLocaleString()}</span> credits for this send.
                        </p>
                    )}

                    {/* What is left on the account, and whether this send fits in
                        it. The composer used to quote the cost without ever
                        saying what the balance was (CG, 2026-09-01: "like an
                        alert if we run out of credits"). */}
                    {!isEmail && smsBalance !== null && (
                        <div className="space-y-1.5">
                            <p className="flex items-center gap-1.5 text-[11px] text-gray-500">
                                <Wallet2 className="w-3.5 h-3.5 text-gray-400" />
                                Credits left on the account:{' '}
                                <span className={`font-semibold ${smsBalanceLow ? 'text-amber-600' : 'text-gray-700'}`}>
                                    {smsBalance.toLocaleString()}
                                </span>
                            </p>
                            {totalCredits > smsBalance ? (
                                <p className="flex items-start gap-1.5 rounded-lg bg-red-50 border border-red-200 px-2.5 py-2 text-[11px] text-red-700">
                                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>
                                        Not enough credits - this send needs about {totalCredits.toLocaleString()} and
                                        only {smsBalance.toLocaleString()} remain. Top up before sending, or the last
                                        buyers on the list will not get the text.
                                    </span>
                                </p>
                            ) : smsBalanceLow && (
                                <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2 text-[11px] text-amber-800">
                                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>Credits are running low. Worth topping up before the next big send.</span>
                                </p>
                            )}
                        </div>
                    )}

                    <p className="text-[11px] text-gray-400">
                        Tip: <code className="px-1 bg-gray-100 rounded">{'{{FirstName}}'}</code> is replaced with each buyer's first name,
                        <code className="px-1 bg-gray-100 rounded">{'{{PreferencesLink}}'}</code> with a link for them to update their
                        own saved search (no login needed), and <code className="px-1 bg-gray-100 rounded">{'{{OffMarketPortalLink}}'}</code>{' '}
                        with the Off Market Portal page at collings.com.au/portal.
                    </p>
                </div>
                <div className="flex-shrink-0 flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100">
                    <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                    <button onClick={send} disabled={sending || reachable === 0}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {isEmail ? 'Send emails' : 'Send texts'}
                    </button>
                </div>
            </div>

            <TemplatePickerModal
                open={showTemplates}
                onClose={() => setShowTemplates(false)}
                templates={templates}
                onPick={applyTemplate}
                channel={channel}
                resolvedTags={resolvedTags}
                zIndex={60}
            />
        </div>

        {/* Deliberately a SIBLING of the overlay, not a child of it. EmailBuilder
            renders full screen through createPortal, and React bubbles portal
            events up the REACT tree rather than the DOM tree - so while it sat
            inside the overlay, every mousedown in the designer that GrapesJS did
            not swallow (a right-click included) reached the overlay and shut the
            whole composer, sending the agent back to the start
            (Paul Jimoulis, CRM-2026-00047). */}
        {builderOpen && (
            <EmailBuilder
                key={builderKey}
                title={`Design email - ${label}`}
                design={designJson}
                html={html}
                preheader={subject}
                onChange={handleBuilderChange}
                onClose={() => setBuilderOpen(false)}
            />
        )}
        </>
    );
}

/**
 * `initialAddress` opens the panel on a property that is NOT a Collings listing
 * (a Re-listing opportunity: Christian, Monday 13354971743, 24 Oct 2026):
 * { address, suburb, state, latitude, longitude, price, beds, baths, cars,
 * property_type }. It goes in as a "Not in Collings" address, so nothing treats
 * its id as a CRM property. `autoRun` runs the search once the suburbs load.
 */
export default function FindBuyersForProperty({ initialProperty = null, initialAddress = null, autoRun = false }) {
    // Export CSV is admin-only — agents shouldn't be able to export buyer data
    // out of the CRM (they still see matches to make their calls).
    const { user } = useSelector((state) => state.auth);
    const isAdmin = hasAdminAccess(user);

    // ── property search (Collings listings + global addresses in one box) ─────
    const [query, setQuery] = useState('');
    const [collingsResults, setCollingsResults] = useState([]);
    const [addressResults, setAddressResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [searching, setSearching] = useState(false);
    const [selectedListing, setSelectedListing] = useState(null); // a Collings listing
    const [selectedAddress, setSelectedAddress] = useState(null); // a global (non-Collings) address

    const [filters, setFilters] = useState({
        suburb: '', state: '',
        priceMin: '', priceMax: '', bedsMin: '', bedsMax: '',
        bathsMin: '', bathsMax: '', carsMin: '', carsMax: '',
        // A list, not a single value — agents can tick similar types (e.g.
        // Apartment, Unit, Villa) all at once as one search.
        propertyTypes: [], portalCategory: '', lat: null, lng: null, radiusKm: 5,
    });

    // ── suburb tick-box ─────────────────────────────────────────────────────
    const [suburbsData, setSuburbsData] = useState(null);
    const [suburbsLoading, setSuburbsLoading] = useState(false);
    // A failed load and a genuinely empty list used to look identical ("No
    // buyers have marked any suburbs yet"), so a 403 read as a fact about the
    // database and the agent had nothing to act on (CRM-2026-00040).
    const [suburbsError, setSuburbsError] = useState(false);
    const [selectedSuburbs, setSelectedSuburbs] = useState(() => new Set());
    const [suburbFilterText, setSuburbFilterText] = useState('');
    const [showAllOther, setShowAllOther] = useState(false);
    const [suburbsOpen, setSuburbsOpen] = useState(false);
    const [suburbDistances, setSuburbDistances] = useState({});
    const OTHER_CAP = 30;

    // ── results ─────────────────────────────────────────────────────────────
    const [results, setResults] = useState(null);
    const [resultsLoading, setResultsLoading] = useState(false);
    const [expanded, setExpanded] = useState(() => new Set());
    const [shortlist, setShortlist] = useState(() => new Set());
    const [actionsBuyer, setActionsBuyer] = useState(null);
    // "Purchased" section (CRM-2026-00049): buyers who have already bought and
    // told the agent they are no longer looking. They are filtered out of the
    // match results server-side, so this tab is the only place they show up.
    const [resultsTab, setResultsTab] = useState('matches'); // 'matches' | 'purchased'
    const [purchasedBuyers, setPurchasedBuyers] = useState([]);
    const [purchasedLoading, setPurchasedLoading] = useState(false);
    const [restoringId, setRestoringId] = useState(null);
    const [purchasingId, setPurchasingId] = useState(null);
    // The "mark Recently Purchased" prompt: { buyer, date } while open, else null.
    const [purchaseDialog, setPurchaseDialog] = useState(null);
    // The "mark Recently rented" prompt (CRM-2026-00132): { buyer, option, date }.
    const [rentDialog, setRentDialog] = useState(null);
    const [rentingId, setRentingId] = useState(null);
    // The match rows "Already purchased" lifted out of the results, with the
    // spot they came from. Without this, "Back to buyers" un-flagged the person
    // server-side but the row never came back on screen, so the buyer sat in
    // neither tab until the agent re-ran the search and looked to have vanished
    // (Joshua Diokno, 2026-09-09). Only ever holds rows from the search that is
    // currently on screen - a new search or a cleared property empties it.
    const removedMatches = useRef([]);
    const [editBuyer, setEditBuyer] = useState(null);
    const [messageChannel, setMessageChannel] = useState(null); // 'email' | 'sms' when composing
    const [showReport, setShowReport] = useState(false); // Buyer Demand PDF composer
    // The match list is no longer capped at 200 (CG, 2026-08-25: "We need to
    // show as many as the database has. We dont want to have a limit"), so a
    // search can return thousands of rows. Every one of them is loaded, held and
    // messaged; we just paint them in batches as the agent scrolls, because
    // dropping 3,000 expanded cards into the DOM at once freezes the tab.
    const [visibleCount, setVisibleCount] = useState(ROWS_PER_BATCH);
    const moreRowsRef = useRef(null);

    // ── last note per matched buyer ─────────────────────────────────────────
    // A buyer's notes were only reachable here by opening the actions popup,
    // and that popup shows the viewing agent's own notes. The property tab's
    // Buyers list stamps every row with the newest note whoever wrote it, so
    // the same buyer looked "noted" there and blank here (Paul Jimoulis,
    // CRM-2026-00050). Same endpoint, same stamp, same buyer.
    const [latestNotesById, setLatestNotesById] = useState({});
    const [latestNotesByEmail, setLatestNotesByEmail] = useState({});

    const searchBoxRef = useRef(null);
    const searchDebounce = useRef(null);
    const suburbDebounce = useRef(null);

    // ── unified property search: Collings listings + global addresses ─────────
    useEffect(() => {
        if (searchDebounce.current) clearTimeout(searchDebounce.current);
        if (!query.trim() || query.trim().length < 2) {
            setCollingsResults([]); setAddressResults([]);
            return;
        }
        searchDebounce.current = setTimeout(async () => {
            const q = query.trim();
            try {
                setSearching(true);
                const [collings, addresses] = await Promise.all([
                    fetchFindBuyersListings(q).then((d) => d.results || []).catch(() => []),
                    searchAddresses(q),
                ]);
                setCollingsResults(collings);
                setAddressResults(addresses);
                setShowDropdown(true);
            } finally {
                setSearching(false);
            }
        }, 300);
        return () => searchDebounce.current && clearTimeout(searchDebounce.current);
    }, [query]);

    // Close the dropdown on outside click.
    useEffect(() => {
        const onClick = (e) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    // When a picked property has no coordinates, look them up via Mapbox so the
    // surrounding-suburb radius can be computed.
    const geocodeIfNeeded = async ({ lat, lng, queryText }) => {
        if (lat != null && lng != null) return;
        if (!queryText || !hasGeocoder()) return;
        const c = await geocodePlace(queryText);
        if (c) setFilters((f) => ({ ...f, lat: c.lat, lng: c.lng }));
    };

    // The search form filled from a property's own details.
    const filtersFrom = (p) => {
        const v = (n) => (n != null ? String(n) : '');
        const listingType = LISTING_TYPE_TO_OPTION[(p.property_type || '').toLowerCase()];
        return {
            suburb: p.suburb || '',
            state: p.state || '',
            // Populate the listing's real price RANGE (price → price_to); beds/baths/cars
            // are single values so min=max. Agent widens for flexibility.
            priceMin: v(p.price), priceMax: v(p.price_to != null ? p.price_to : p.price),
            bedsMin: v(p.beds), bedsMax: v(p.beds),
            bathsMin: v(p.baths), bathsMax: v(p.baths),
            carsMin: v(p.cars), carsMax: v(p.cars),
            propertyTypes: listingType ? [listingType] : [],
            portalCategory: p.portal_category || '',
            lat: p.latitude,
            lng: p.longitude,
            radiusKm: filters.radiusKm,
        };
    };

    const pickListing = (p) => {
        setSelectedListing(p);
        setSelectedAddress(null);
        setFilters(filtersFrom(p));
        setQuery('');
        setShowDropdown(false);
        removedMatches.current = [];
        setResults(null);
        geocodeIfNeeded({
            lat: p.latitude, lng: p.longitude,
            queryText: [p.address || p.suburb, p.state, 'Australia'].filter(Boolean).join(', '),
        });
    };

    const pickOutsideProperty = (p) => {
        setSelectedAddress({
            description: p.address || [p.suburb, p.state].filter(Boolean).join(', '),
            suburb: p.suburb || '', state: p.state || '', lat: p.latitude, lng: p.longitude,
        });
        setSelectedListing(null);
        setFilters({ ...filtersFrom(p), portalCategory: '' });
        removedMatches.current = [];
        setResults(null);
        geocodeIfNeeded({
            lat: p.latitude, lng: p.longitude,
            queryText: [p.address || p.suburb, p.state, 'Australia'].filter(Boolean).join(', '),
        });
    };

    // When embedded on a property page, pre-select that property so the whole form
    // (suburb, price range, beds/baths/cars, type) is auto-filled from its details.
    // The agent just ticks suburbs and hits "Find buyers". Runs once on mount.
    const initialApplied = useRef(false);
    useEffect(() => {
        if (initialApplied.current) return;
        if (initialProperty && initialProperty.id) {
            initialApplied.current = true;
            pickListing(initialProperty);
        } else if (initialAddress && initialAddress.suburb) {
            initialApplied.current = true;
            pickOutsideProperty(initialAddress);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialProperty, initialAddress]);

    const pickAddress = (sugg) => {
        const f = extractAddressFields(sugg);
        setSelectedAddress({ description: sugg.description, ...f });
        setSelectedListing(null);
        setFilters((prev) => ({
            ...prev,
            suburb: f.suburb || prev.suburb,
            state: f.state || prev.state,
            portalCategory: '',
            lat: f.lat,
            lng: f.lng,
        }));
        setQuery('');
        setShowDropdown(false);
        removedMatches.current = [];
        setResults(null);
        geocodeIfNeeded({
            lat: f.lat, lng: f.lng,
            queryText: sugg.description || [f.suburb, f.state, 'Australia'].filter(Boolean).join(', '),
        });
    };

    const clearProperty = () => {
        setSelectedListing(null);
        setSelectedAddress(null);
        setFilters((f) => ({ ...f, lat: null, lng: null }));
        removedMatches.current = [];
        setResults(null);
    };

    // ── load suburb tick-box whenever suburb / state / radius change ──────────
    const loadSuburbs = useCallback(async () => {
        if (!filters.suburb.trim()) { setSuburbsData(null); setSuburbsError(false); return; }
        try {
            setSuburbsLoading(true);
            setSuburbsError(false);
            const data = await fetchFindBuyersSuburbs({
                suburb: filters.suburb.trim(),
                state: filters.state.trim(),
                lat: filters.lat,
                lng: filters.lng,
                radiusKm: filters.radiusKm,
            });

            // If we have an origin coordinate, geocode the marked suburbs (Mapbox,
            // cached) and compute distances so surrounding suburbs get pre-ticked.
            let subs = data.suburbs || [];
            let distances = {};
            if (filters.lat != null && filters.lng != null) {
                const enriched = await enrichSuburbsWithDistance(
                    subs, { lat: filters.lat, lng: filters.lng }, filters.radiusKm, filters.state.trim()
                );
                subs = enriched.suburbs;
                distances = enriched.distances;
                if (Object.keys(distances).length) data.origin_resolved = true;
            }

            setSuburbDistances(distances);
            setSuburbsData({ ...data, suburbs: subs });
            // Pre-tick recommended (target + within radius).
            setSelectedSuburbs(new Set(subs.filter((s) => s.recommended).map((s) => s.suburb)));
        } catch {
            setSuburbsData(null);
            setSuburbsError(true);
        } finally {
            setSuburbsLoading(false);
        }
    }, [filters.suburb, filters.state, filters.lat, filters.lng, filters.radiusKm]);

    useEffect(() => {
        if (suburbDebounce.current) clearTimeout(suburbDebounce.current);
        suburbDebounce.current = setTimeout(() => loadSuburbs(), 350);
        return () => suburbDebounce.current && clearTimeout(suburbDebounce.current);
    }, [loadSuburbs]);

    const toggleSuburb = (name) => {
        setSelectedSuburbs((prev) => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            return next;
        });
    };

    const groupedSuburbs = useMemo(() => {
        const rows = (suburbsData?.suburbs || []).filter(
            (s) => !suburbFilterText.trim() || s.suburb.toLowerCase().includes(suburbFilterText.trim().toLowerCase())
        );
        return {
            exact: rows.filter((s) => s.kind === 'exact'),
            surrounding: rows.filter((s) => s.kind === 'surrounding'),
            other: rows.filter((s) => s.kind === 'other'),
        };
    }, [suburbsData, suburbFilterText]);

    // ── run the match ─────────────────────────────────────────────────────────
    // The criteria the backend needs. Shared by the results search and the Buyer
    // Demand PDF, so the report can never describe a different search from the
    // one on screen.
    const searchPayload = useMemo(() => {
        const num = (v) => (v !== '' && v != null ? Number(v) : null);
        return {
            property_id: selectedListing?.id,
            suburb: filters.suburb.trim(),
            state: filters.state.trim(),
            lat: filters.lat,
            lng: filters.lng,
            radius_km: filters.radiusKm,
            price_min: num(filters.priceMin),
            price_max: num(filters.priceMax),
            beds_min: num(filters.bedsMin),
            beds_max: num(filters.bedsMax),
            baths_min: num(filters.bathsMin),
            baths_max: num(filters.bathsMax),
            cars_min: num(filters.carsMin),
            cars_max: num(filters.carsMax),
            property_types: filters.propertyTypes,
            portal_category: filters.portalCategory,
            // The tick-list gates the match, so sending an empty one matches
            // nobody. When the picker had nothing to tick, because it failed
            // to load or no buyer has marked a suburb yet, fall back to the
            // agent typed. The obvious search still runs instead of the panel
            // dead-ending on a disabled button (CRM-2026-00040).
            suburbs: selectedSuburbs.size
                ? Array.from(selectedSuburbs)
                : [filters.suburb.trim()].filter(Boolean),
            suburb_distances: suburbDistances,
        };
    }, [selectedListing, filters, selectedSuburbs, suburbDistances]);

    const runSearch = async () => {
        if (!searchPayload.suburbs.length) {
            toast.error('Enter the suburb the property is in first.');
            return;
        }
        try {
            setResultsLoading(true);
            const data = await findBuyersForProperty(searchPayload);
            removedMatches.current = [];
            setResults(data);
            // A search always lands on its own results, even if the agent was
            // reading the Purchased tab when they pressed the button.
            setResultsTab('matches');
            setExpanded(new Set());
            setShortlist(new Set());
            setVisibleCount(ROWS_PER_BATCH);
        } catch {
            toast.error('Could not load buyers. Please try again.');
        } finally {
            setResultsLoading(false);
        }
    };

    // Opened from a property's own Find Buyers button: show the matches straight
    // away, with the suburbs the picker pre-ticked. Once only; after that the
    // agent drives it.
    const autoRan = useRef(false);
    useEffect(() => {
        if (!autoRun || autoRan.current || suburbsLoading || !filters.suburb.trim()) return;
        if (!suburbsData && !suburbsError) return;
        autoRan.current = true;
        runSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoRun, suburbsLoading, suburbsData, suburbsError]);

    // Re-run the search that is already on screen and keep the agent where they
    // are: same tab, same scroll depth, same ticked rows. Used when a buyer is
    // put back but the row cannot be spliced in from memory, because the agent
    // left the page and searched again in between (Joshua Diokno, 2026-09-11:
    // he filed Stephanie Fernandes away, went off to check Who to Call and
    // Communications, came back, searched again, and "Back to buyers" left her
    // in neither tab). Asking the server is the only way to know where she sits
    // in a result set this component never saw her leave.
    const refetchMatches = async () => {
        if (!searchPayload.suburbs.length) return null;
        try {
            setResultsLoading(true);
            const data = await findBuyersForProperty(searchPayload);
            // The fresh list is the server's truth about who is still a buyer,
            // so every row this component was holding back is accounted for.
            removedMatches.current = [];
            setResults(data);
            // Keep the painted window at least as deep as it was, so a restored
            // row near the bottom is not hidden behind "Show more".
            setVisibleCount((v) => Math.max(v, ROWS_PER_BATCH));
            return data;
        } catch {
            return null;
        } finally {
            setResultsLoading(false);
        }
    };

    // Paint the next batch of cards as the agent reaches the bottom of the list,
    // so scrolling just keeps going instead of stopping at a page limit. Falls
    // back to the "Show more" button when IntersectionObserver isn't available.
    const totalRows = results?.results?.length || 0;
    useEffect(() => {
        if (visibleCount >= totalRows) return undefined;
        const el = moreRowsRef.current;
        if (!el || typeof window.IntersectionObserver !== 'function') return undefined;
        const observer = new window.IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                setVisibleCount((c) => Math.min(c + ROWS_PER_BATCH, totalRows));
            }
        }, { rootMargin: '600px' });
        observer.observe(el);
        return () => observer.disconnect();
    }, [visibleCount, totalRows]);

    // Batch-fetch the newest note for the rows currently painted. Only the
    // visible batch is asked for (a search can return thousands), and each
    // person is only ever asked for once per result set.
    const notesAskedRef = useRef({ forResults: null, keys: new Set() });
    useEffect(() => {
        const rows = results?.results?.slice(0, visibleCount) || [];
        const asked = notesAskedRef.current;
        if (asked.forResults !== results) {
            // New search — drop the previous list's stamps.
            notesAskedRef.current = { forResults: results, keys: new Set() };
            setLatestNotesById({});
            setLatestNotesByEmail({});
        }
        const keys = notesAskedRef.current.keys;
        const ids = new Set();
        const emails = new Set();
        rows.forEach((b) => {
            if (b.contact_id != null && !keys.has(`c:${b.contact_id}`)) {
                keys.add(`c:${b.contact_id}`);
                ids.add(b.contact_id);
            }
            const email = (b.email || '').trim().toLowerCase();
            if (email && email.includes('@') && !keys.has(`e:${email}`)) {
                keys.add(`e:${email}`);
                emails.add(b.email.trim());
            }
        });
        if (ids.size === 0 && emails.size === 0) return;

        // Deliberately no cancel flag: the state updates below are merges, so a
        // reply that lands late is still correct, and dropping it would leave
        // the row unstamped for good (the person is already marked as asked).
        (async () => {
            try {
                const res = await getLatestNotes({ contactIds: [...ids], emails: [...emails] });
                setLatestNotesById((prev) => ({ ...prev, ...(res?.data?.by_contact_id || {}) }));
                setLatestNotesByEmail((prev) => {
                    const next = { ...prev };
                    Object.entries(res?.data?.by_email || {}).forEach(([k, v]) => {
                        next[(k || '').toLowerCase()] = v;
                    });
                    return next;
                });
            } catch (err) {
                // Non-critical: the match cards still render, just unstamped.
                // Forget the failed batch so the next scroll can try again.
                ids.forEach((id) => keys.delete(`c:${id}`));
                emails.forEach((e) => keys.delete(`e:${e.trim().toLowerCase()}`));
                console.error('Failed to fetch latest notes', err);
            }
        })();
    }, [results, visibleCount]);

    // Newest note for a match row — contact id first, email as the fallback for
    // portal buyers who aren't a CRM contact yet.
    const latestNoteFor = (b) => {
        if (!b) return null;
        const byId = b.contact_id != null ? latestNotesById[String(b.contact_id)] : null;
        if (byId) return byId;
        const email = (b.email || '').trim().toLowerCase();
        return email ? (latestNotesByEmail[email] || null) : null;
    };

    // Show a note the agent just added straight away, without re-running the search.
    // The Purchased section, loaded once on mount so the tab can carry a count
    // before the agent has run any search.
    const loadPurchased = useCallback(async () => {
        setPurchasedLoading(true);
        try {
            const data = await fetchPurchasedBuyers();
            setPurchasedBuyers(data?.results || []);
        } catch (e) {
            setPurchasedBuyers([]);
        } finally {
            setPurchasedLoading(false);
        }
    }, []);

    useEffect(() => { loadPurchased(); }, [loadPurchased]);

    // The candidate sources each know a buyer by a different key, so a row and a
    // purchased record are the same person when any one of them lines up.
    const buyerKeys = (row) => {
        const keys = [];
        if (row?.contact_id != null) keys.push(`c${row.contact_id}`);
        if (row?.buyer_id != null) keys.push(`b${row.buyer_id}`);
        const email = (row?.email || '').trim().toLowerCase();
        if (email) keys.push(`e${email}`);
        return keys;
    };

    // Drop a buyer back into the match list exactly where they were sitting
    // before "Already purchased" lifted them out. Returns false when there is
    // nothing to put back — no search on screen, or the results were replaced
    // since — so the caller can say so instead of claiming a row reappeared.
    const restoreMatchRow = (keys) => {
        if (!results) return false;
        const hit = removedMatches.current.find((e) => e.keys.some((k) => keys.includes(k)));
        if (!hit) return false;
        removedMatches.current = removedMatches.current.filter((e) => e !== hit);
        const at = Math.min(Math.max(hit.index, 0), (results.results || []).length);
        setResults((prev) => {
            if (!prev) return prev;
            const next = [...(prev.results || [])];
            next.splice(Math.min(at, next.length), 0, hit.row);
            return { ...prev, results: next, count: next.length };
        });
        // Rows are painted in batches as the agent scrolls; make sure the one
        // just restored is inside the painted window.
        setVisibleCount((v) => Math.max(v, at + 1));
        return true;
    };

    // A buyer the agent files away (Purchased or Recently rented) leaves the
    // results immediately - the agent is on the phone and should not have to
    // re-run the search to see them go. The server already excludes them from
    // the next search. Undoing it puts them back.
    const moveMatchRow = (b, filedAway) => {
        const keys = buyerKeys({ contact_id: b?.contactId, buyer_id: b?.id, email: b?.email });
        if (filedAway) {
            const rows = results?.results || [];
            const kept = [];
            const lifted = [];
            rows.forEach((row, index) => {
                const rowKeys = buyerKeys(row);
                if (rowKeys.some((k) => keys.includes(k))) {
                    lifted.push({ row, index, keys: rowKeys });
                } else {
                    kept.push(row);
                }
            });
            if (lifted.length) {
                removedMatches.current = [...removedMatches.current, ...lifted];
                setResults((prev) => (prev ? { ...prev, results: kept, count: kept.length } : prev));
            }
        } else if (!restoreMatchRow(keys) && results) {
            // The popup's own "Back to buyers", pressed on a row that is still
            // in front of the agent. If this component never saw them leave,
            // ask the server for the list again rather than doing nothing.
            refetchMatches();
        }
    };

    const handlePurchasedChange = (b, purchased) => {
        moveMatchRow(b, purchased);
        loadPurchased();
    };

    // "Back to buyers" from inside the Purchased tab.
    const restoreBuyer = async (row) => {
        const key = row.contact_id ?? `b${row.buyer_id}`;
        setRestoringId(key);
        try {
            await setBuyerPurchased({
                contactId: row.contact_id || undefined,
                buyerId: row.buyer_id || undefined,
                purchased: false,
            });
            setPurchasedBuyers((prev) => prev.filter((r) => r !== row));
            const keys = buyerKeys(row);
            let backInList = restoreMatchRow(keys);
            // The row is only in memory for the search that lifted it out. Once
            // the agent has left the page or searched again, the stash is empty
            // and the buyer would land in neither tab, which is what Joshua hit
            // twice. Re-run the search instead of telling him to.
            if (!backInList && results) {
                const data = await refetchMatches();
                backInList = (data?.results || []).some(
                    (r) => buyerKeys(r).some((k) => keys.includes(k)));
            }
            toast.success(backInList
                ? `${row.name} is back in Matches`
                : `${row.name} is back in your buyer lists. Run the search again to see them under Matches.`);
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Could not restore this buyer');
        } finally {
            setRestoringId(null);
        }
    };

    // "Mark as Recently Purchased" straight from a match row — the same action as
    // the buyer popup's, surfaced on the row so agents don't have to open the
    // popup to find it (CRM-2026-00068). Opens a small prompt for the optional
    // purchase date; reversible from the Purchased tab.
    const handleMarkPurchased = (b) => {
        if (!b.contact_id && !b.buyer_id) {
            toast.error('Could not identify this buyer yet — try again in a moment.');
            return;
        }
        setPurchaseDialog({ buyer: b, date: '' });
    };

    const submitMarkPurchased = async () => {
        const b = purchaseDialog?.buyer;
        if (!b) return;
        const key = rowId(b);
        setPurchasingId(key);
        try {
            await setBuyerPurchased({
                contactId: b.contact_id || undefined,
                buyerId: b.buyer_id || undefined,
                purchased: true,
                purchaseDate: purchaseDialog.date || '',
            });
            handlePurchasedChange({ contactId: b.contact_id, id: b.buyer_id, email: b.email }, true);
            toast.success(`${b.name} moved to Recently Purchased — they'll stop showing as a buyer`);
            setPurchaseDialog(null);
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Could not update this buyer');
        } finally {
            setPurchasingId(null);
        }
    };

    // "Recently rented" from a match row (CRM-2026-00132): renting for now,
    // back to buying later. Same action as the buyer popup's.
    const handleMarkRented = (b) => {
        if (!b.contact_id && !b.buyer_id) {
            toast.error('Could not identify this buyer yet — try again in a moment.');
            return;
        }
        setRentDialog({ buyer: b, option: DEFAULT_RENTED_RETURN, date: '' });
    };

    const submitMarkRented = async () => {
        const b = rentDialog?.buyer;
        if (!b) return;
        if (rentDialog.option === 'date' && !rentDialog.date) {
            toast.error('Pick the date they should come back to buying.');
            return;
        }
        setRentingId(rowId(b));
        try {
            const data = await setBuyerRented({
                contactId: b.contact_id || undefined,
                buyerId: b.buyer_id || undefined,
                rented: true,
                returnOption: rentDialog.option,
                returnDate: rentDialog.date,
            });
            moveMatchRow({ contactId: b.contact_id, id: b.buyer_id, email: b.email }, true);
            toast.success(`${b.name} moved to Recently rented. You'll get a reminder to call them on ${formatMelbourneShortDate(data?.status?.paused_until)}`);
            setRentDialog(null);
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Could not update this buyer');
        } finally {
            setRentingId(null);
        }
    };

    const applyLatestNote = (b, note) => {
        if (!note) return;
        if (b?.contact_id != null) {
            setLatestNotesById((prev) => ({ ...prev, [String(b.contact_id)]: note }));
        }
        const email = (b?.email || '').trim().toLowerCase();
        if (email) setLatestNotesByEmail((prev) => ({ ...prev, [email]: note }));
    };

    // The amber "Last note" stamp, identical to the one on a property's Buyers
    // tab: the newest note, who left it and when.
    const renderLastNote = (b) => {
        const note = latestNoteFor(b);
        if (!note?.text) return null;
        const stamp = [
            note.author,
            note.created_at
                ? new Date(note.created_at).toLocaleString('en-AU', {
                    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                })
                : null,
        ].filter(Boolean).join(' · ');
        return (
            <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-100 px-2.5 py-1.5">
                <StickyNote className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                <div className="min-w-0">
                    <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-0.5">Last note</p>
                    <p className="text-[12px] text-amber-900 whitespace-pre-wrap break-words">{note.text}</p>
                    {stamp && <p className="text-[10px] font-semibold text-amber-700/80 mt-0.5">{stamp}</p>}
                </div>
            </div>
        );
    };

    const toggleShortlist = (id) => {
        setShortlist((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const shortlistRows = useMemo(
        () => (results?.results || []).filter((r) => shortlist.has(rowId(r))),
        [results, shortlist]
    );

    // Who a "message all" send goes to: the shortlist when the agent has ticked
    // some, otherwise every match (mirrors the CSV export behaviour).
    const messageRecipients = useMemo(
        () => (shortlistRows.length ? shortlistRows : (results?.results || [])),
        [shortlistRows, results]
    );

    // A human label for the property, used to seed the default message.
    const propertyLabel = useMemo(() => (
        selectedListing?.address
        || selectedAddress?.description
        || [filters.suburb, filters.state].filter(Boolean).join(', ')
        || 'this property'
    ), [selectedListing, selectedAddress, filters.suburb, filters.state]);

    const copyEmails = () => {
        const emails = shortlistRows.map((r) => r.email).filter(Boolean);
        if (!emails.length) { toast.error('No emails in the shortlist.'); return; }
        navigator.clipboard.writeText(emails.join(', '));
        toast.success(`Copied ${emails.length} email${emails.length > 1 ? 's' : ''}`);
    };

    const exportCsv = () => {
        const rows = shortlistRows.length ? shortlistRows : (results?.results || []);
        if (!rows.length) return;
        const header = ['Name', 'Email', 'Phone', 'Match %', 'Suburb match', 'Matched suburb', 'Intent'];
        const body = rows.map((r) => [
            r.name, r.email, r.phone, r.match_score, r.suburb_match, r.matched_suburb, r.intent_score,
        ].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
        const csv = [header.join(','), ...body].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `buyers-${(filters.suburb || 'property').toLowerCase().replace(/\s+/g, '-')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-lg font-bold text-gray-900">Find Buyers for a Property</h3>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                    Set the requirements for a property (it doesn’t need to be in Collings yet)
                    and tick the suburbs that count, including buyers who marked{' '}
                    <span className="font-medium">surrounding suburbs</span> or a broad area like
                    “Melbourne”. Searches your <span className="font-medium">whole buyer database</span>{' '}
                    (portal, CRM and imported), not just portal buyers.
                </p>
            </div>

            <div className="p-5 space-y-6">
                {/* Step 1: property (one search: Collings listings + global addresses) */}
                <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        1 · Property
                    </div>
                    {!selectedListing && !selectedAddress ? (
                        <div className="relative" ref={searchBoxRef}>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onFocus={() => (collingsResults.length || addressResults.length) && setShowDropdown(true)}
                                    placeholder="Search any address, or one of your Collings listings…"
                                    className="w-full pl-9 pr-9 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                                />
                                {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />}
                            </div>
                            {showDropdown && (collingsResults.length > 0 || addressResults.length > 0) && (
                                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto">
                                    {collingsResults.length > 0 && (
                                        <>
                                            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-600">In Collings</div>
                                            {collingsResults.map((p) => (
                                                <button key={`c-${p.id}`} onClick={() => pickListing(p)}
                                                    className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 border-b border-gray-50 last:border-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-medium text-gray-900 truncate">{p.address || p.suburb}</span>
                                                        <span className="text-[10px] font-medium text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded shrink-0">In Collings</span>
                                                    </div>
                                                    <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 mt-0.5">
                                                        <span>{[p.suburb, p.state].filter(Boolean).join(', ')}</span>
                                                        {p.price != null && <span>{fmtMoney(p.price)}</span>}
                                                        {p.beds != null && <span>{p.beds} bed</span>}
                                                        {p.property_type && <span className="capitalize">{p.property_type}</span>}
                                                    </div>
                                                </button>
                                            ))}
                                        </>
                                    )}
                                    {addressResults.length > 0 && (
                                        <>
                                            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Other addresses (not in Collings)</div>
                                            {addressResults.map((a, i) => (
                                                <button key={`a-${i}`} onClick={() => pickAddress(a)}
                                                    className="w-full text-left px-3 py-2.5 hover:bg-indigo-50 border-b border-gray-50 last:border-0">
                                                    <div className="flex items-center gap-2">
                                                        <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                                        <span className="text-sm text-gray-800 truncate">{a.description}</span>
                                                    </div>
                                                </button>
                                            ))}
                                        </>
                                    )}
                                </div>
                            )}
                            <p className="text-xs text-gray-400 mt-1.5">
                                Finding buyers for a new listing? Pick its address here; it doesn’t need to be in Collings yet.
                            </p>
                        </div>
                    ) : (
                        <div className="flex items-start justify-between gap-3 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
                            <div className="flex items-start gap-2.5 min-w-0">
                                <Home className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-gray-900 truncate">
                                            {selectedListing ? (selectedListing.address || selectedListing.suburb) : selectedAddress.description}
                                        </span>
                                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0 ${selectedListing ? 'text-emerald-700 bg-emerald-100' : 'text-gray-600 bg-gray-100'}`}>
                                            {selectedListing ? 'In Collings' : 'Not in Collings'}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 mt-0.5">
                                        {selectedListing ? (
                                            <>
                                                <span>{[selectedListing.suburb, selectedListing.state].filter(Boolean).join(', ')}</span>
                                                {selectedListing.price != null && <span>{fmtMoney(selectedListing.price)}</span>}
                                                {selectedListing.beds != null && <span>{selectedListing.beds} bed</span>}
                                            </>
                                        ) : (
                                            <span>{[selectedAddress.suburb, selectedAddress.state, selectedAddress.postcode].filter(Boolean).join(', ') || 'Set the requirements below'}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <button onClick={clearProperty} className="text-gray-400 hover:text-gray-600 shrink-0" title="Change property">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>

                {/* Step 2: requirements */}
                <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        2 · Requirements
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="text-xs text-gray-500">
                            <span className="flex items-center gap-1 mb-1"><MapPin className="w-3 h-3" /> Suburb</span>
                            <SuburbAutocomplete
                                value={filters.suburb}
                                onChange={(e) => setFilter('suburb', e.target.value)}
                                onSelect={(picked) => setFilters((f) => ({
                                    ...f,
                                    suburb: picked.suburb || f.suburb,
                                    state: picked.state || f.state,
                                }))}
                                placeholder="e.g. Fawkner"
                                className="w-full px-2.5 py-2 pr-8 border border-gray-300 rounded-lg text-sm text-gray-900"
                            />
                        </label>
                        <label className="text-xs text-gray-500">
                            <span className="block mb-1">State</span>
                            <input value={filters.state} onChange={(e) => setFilter('state', e.target.value)}
                                className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm text-gray-900" placeholder="VIC" />
                        </label>
                        <RangeField icon={Wallet} label="Price ($)" step="10000"
                            minVal={filters.priceMin} maxVal={filters.priceMax}
                            onMin={(v) => setFilter('priceMin', v)} onMax={(v) => setFilter('priceMax', v)} />
                        <RangeField icon={BedDouble} label="Bedrooms"
                            minVal={filters.bedsMin} maxVal={filters.bedsMax}
                            onMin={(v) => setFilter('bedsMin', v)} onMax={(v) => setFilter('bedsMax', v)} />
                        <RangeField icon={Bath} label="Bathrooms"
                            minVal={filters.bathsMin} maxVal={filters.bathsMax}
                            onMin={(v) => setFilter('bathsMin', v)} onMax={(v) => setFilter('bathsMax', v)} />
                        <RangeField icon={Car} label="Car spaces"
                            minVal={filters.carsMin} maxVal={filters.carsMax}
                            onMin={(v) => setFilter('carsMin', v)} onMax={(v) => setFilter('carsMax', v)} />
                        <label className="text-xs text-gray-500">
                            <span className="flex items-center gap-1 mb-1"><Compass className="w-3 h-3" /> Radius</span>
                            <select value={filters.radiusKm} onChange={(e) => setFilter('radiusKm', Number(e.target.value))}
                                className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white">
                                {RADIUS_OPTIONS.map((r) => <option key={r} value={r}>{r} km</option>)}
                            </select>
                        </label>
                    </div>
                    <div className="mt-3">
                        <span className="flex items-center gap-1 mb-1.5 text-xs text-gray-500"><Building2 className="w-3 h-3" /> Type</span>
                        <PropertyTypeTiles value={filters.propertyTypes} onChange={(v) => setFilter('propertyTypes', v)} accent="indigo" />
                        <p className="text-xs text-gray-400 mt-1.5">Tick as many as apply, e.g. Apartment, Unit and Villa together — leave none ticked to match any type.</p>
                    </div>
                    <p className="text-xs text-gray-400 mt-1.5">Leave a field blank to ignore it. Budget matching uses a 20% range: buyers whose stated budget is more than 20% away from the price are left out. (Buyers who never set a budget still show.)</p>
                </div>

                {/* Step 3: suburbs tick-box (collapsed by default so it doesn't hog space) */}
                <div>
                    <button type="button" onClick={() => setSuburbsOpen((o) => !o)}
                        className="w-full flex items-center justify-between mb-1 group">
                        <div className="flex items-center gap-1.5">
                            {suburbsOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">3 · Suburbs buyers have marked</span>
                        </div>
                        <span className="text-xs text-gray-400">
                            {!filters.suburb.trim() ? 'enter a suburb'
                                : suburbsLoading ? 'loading…'
                                    : suburbsError ? 'couldn’t load'
                                        : `${selectedSuburbs.size} ticked${!suburbsOpen ? ' · click to choose' : ''}`}
                        </span>
                    </button>

                    {/* Collapsed preview of what's ticked */}
                    {!suburbsOpen && selectedSuburbs.size > 0 && (
                        <div className="text-xs text-gray-500 mb-1 pl-5 truncate">
                            {Array.from(selectedSuburbs).slice(0, 6).join(', ')}
                            {selectedSuburbs.size > 6 ? ` +${selectedSuburbs.size - 6} more` : ''}
                        </div>
                    )}

                    {suburbsOpen && (!filters.suburb.trim() ? (
                        <div className="text-sm text-gray-400 italic py-3">Enter a suburb above to see who’s looking nearby.</div>
                    ) : suburbsLoading ? (
                        <div className="flex items-center gap-2 text-sm text-gray-400 py-3"><Loader2 className="w-4 h-4 animate-spin" /> Loading suburbs…</div>
                    ) : suburbsError ? (
                        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-center gap-3">
                            <span>We couldn’t load the suburbs buyers have marked. You can still search on <strong>{filters.suburb.trim()}</strong> itself.</span>
                            <button type="button" onClick={loadSuburbs}
                                className="shrink-0 text-xs font-semibold text-amber-800 underline hover:no-underline">
                                Try again
                            </button>
                        </div>
                    ) : !suburbsData || !(suburbsData.suburbs || []).length ? (
                        <div className="text-sm text-gray-400 italic py-3">
                            No buyers have marked any suburbs yet. Searching <strong>{filters.suburb.trim()}</strong> on its own.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {!suburbsData.origin_resolved && (
                                <div className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                                    We couldn’t find map coordinates for this suburb, so nothing is auto-ticked by distance. Tick the relevant areas by hand below.
                                </div>
                            )}
                            {(suburbsData.suburbs || []).length > 8 && (
                                <input value={suburbFilterText} onChange={(e) => setSuburbFilterText(e.target.value)}
                                    placeholder="Filter suburbs…"
                                    className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm" />
                            )}
                            {['exact', 'surrounding', 'other'].map((kind) => {
                                const allRows = groupedSuburbs[kind];
                                if (!allRows.length) return null;
                                const meta = SUBURB_KIND[kind];
                                const capped = kind === 'other' && !showAllOther && !suburbFilterText.trim();
                                const rows = capped ? allRows.slice(0, OTHER_CAP) : allRows;
                                return (
                                    <div key={kind}>
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${meta.chip}`}>{meta.label}</span>
                                            {kind === 'other' && <span className="text-[11px] text-gray-400">buyers who marked a broader area (e.g. just “Melbourne”), sorted by demand</span>}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {rows.map((s) => {
                                                const on = selectedSuburbs.has(s.suburb);
                                                return (
                                                    <button key={s.suburb} onClick={() => toggleSuburb(s.suburb)}
                                                        className={`inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-lg border text-sm transition ${on ? 'bg-indigo-50 border-indigo-300 text-indigo-800' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                                                        <span className={`w-4 h-4 rounded flex items-center justify-center border ${on ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                                                            {on && <Check className="w-3 h-3 text-white" />}
                                                        </span>
                                                        <span className="font-medium">{s.suburb}</span>
                                                        <span className="text-xs text-gray-400">
                                                            {/* Name the state when it isn't the one being searched, so an
                                                                interstate area reads as one instead of looking like a neighbour. */}
                                                            {s.interstate && s.state ? `${s.state} · ` : ''}
                                                            {s.distance_km != null ? `${s.distance_km}km · ` : ''}{s.buyer_count} buyer{s.buyer_count !== 1 ? 's' : ''}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {capped && allRows.length > OTHER_CAP && (
                                            <button onClick={() => setShowAllOther(true)}
                                                className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-700">
                                                Show all {allRows.length} areas
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                {/* Run */}
                <div className="flex flex-wrap items-center gap-3 pt-1">
                    <button onClick={runSearch} disabled={resultsLoading || !searchPayload.suburbs.length}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        {resultsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                        Find buyers
                    </button>
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                        {filters.suburb.trim() && selectedSuburbs.size === 0
                            ? `Searching ${filters.suburb.trim()} only. Open step 3 to add surrounding suburbs.`
                            : 'Only buyers who meet every criterion you set are shown. Leave a field blank to ignore it.'}
                    </span>
                </div>
            </div>

            {/* Matches | Purchased. The Purchased tab is always reachable, even
                before a search has been run, so an agent can review (and undo)
                the buyers they filed away on the phone. CRM-2026-00049. */}
            <div className="border-t border-gray-100 px-5 pt-4">
                <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
                    <button
                        type="button"
                        onClick={() => setResultsTab('matches')}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                            resultsTab === 'matches'
                                ? 'bg-white text-indigo-700 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}>
                        <Users className="w-3.5 h-3.5" /> Matches
                        {results ? ` (${results.count.toLocaleString()})` : ''}
                    </button>
                    <button
                        type="button"
                        onClick={() => setResultsTab('purchased')}
                        title="Buyers who have already bought and are no longer looking"
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                            resultsTab === 'purchased'
                                ? 'bg-white text-amber-700 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}>
                        <ShoppingBag className="w-3.5 h-3.5" /> Purchased ({purchasedBuyers.length.toLocaleString()})
                    </button>
                </div>
            </div>

            {resultsTab === 'purchased' && (
                <div className="border-t border-gray-100 mt-4 p-5">
                    <p className="text-sm text-gray-600 mb-4">
                        These buyers told an agent they have already purchased and are no
                        longer looking, so they are left out of every match, suburb count
                        and buyer audience. Put anyone back with one click.
                    </p>
                    {purchasedLoading ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                        </div>
                    ) : purchasedBuyers.length === 0 ? (
                        <div className="text-sm text-gray-500 py-6 text-center">
                            Nobody here yet. When a buyer tells you they have bought, open
                            them from the results and press <span className="font-semibold">Recently Purchased</span>.
                        </div>
                    ) : (
                        <div className="space-y-2.5">
                            {purchasedBuyers.map((row) => {
                                const key = row.contact_id ?? `b${row.buyer_id}`;
                                return (
                                    <div key={key} className="rounded-xl border border-amber-100 bg-amber-50/50 p-3 flex flex-wrap items-center gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="font-semibold text-gray-900 truncate">{row.name}</div>
                                            <div className="text-xs text-gray-600 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                                                {row.email && (
                                                    <a href={`mailto:${row.email}`} className="inline-flex items-center gap-1 hover:text-indigo-700">
                                                        <Mail className="w-3 h-3" /> {row.email}
                                                    </a>
                                                )}
                                                {row.phone && (
                                                    <a href={`tel:${row.phone}`} className="inline-flex items-center gap-1 hover:text-indigo-700">
                                                        <Phone className="w-3 h-3" /> {row.phone}
                                                    </a>
                                                )}
                                                {row.purchase_date && <span>Bought {row.purchase_date}</span>}
                                                {!row.purchase_date && row.marked_at && (
                                                    <span>Filed {formatMelbourneDatePart(row.marked_at)}</span>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => restoreBuyer(row)}
                                            disabled={restoringId === key}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-amber-300 text-amber-800 text-xs font-semibold hover:bg-amber-100 disabled:opacity-50">
                                            {restoringId === key
                                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                : <Undo2 className="w-3.5 h-3.5" />}
                                            Back to buyers
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Results */}
            {resultsTab === 'matches' && results && (
                <div className="border-t border-gray-100 p-5 mt-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <div className="text-sm text-gray-700">
                            <span className="font-bold text-gray-900">{results.count.toLocaleString()}</span> potential buyer{results.count !== 1 ? 's' : ''}
                            {results.count > 0 && ' · best fit first'}
                            {visibleCount < totalRows && (
                                <span className="text-gray-400"> · showing {visibleCount.toLocaleString()}, scroll for more</span>
                            )}
                        </div>
                        {results.count > 0 && (
                            <div className="flex flex-wrap items-center gap-2">
                                {shortlist.size > 0 && (
                                    <>
                                        <span className="text-xs text-gray-500">{shortlist.size} selected</span>
                                        <button onClick={copyEmails} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 hover:bg-gray-50">
                                            <Copy className="w-3.5 h-3.5" /> Copy emails
                                        </button>
                                    </>
                                )}
                                {/* The vendor-facing "we already have N buyers" PDF for a
                                    listing presentation. Aggregated server-side, so it never
                                    carries a buyer's name or contact details. */}
                                <button onClick={() => setShowReport(true)}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-teal-600 text-white text-xs font-semibold hover:bg-teal-700">
                                    <FileText className="w-3.5 h-3.5" /> Generate Vendor Buyer Report
                                </button>
                                <button onClick={() => setMessageChannel('email')}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700">
                                    <Mail className="w-3.5 h-3.5" /> Email {shortlist.size > 0 ? 'selected' : 'all'}
                                </button>
                                <button onClick={() => setMessageChannel('sms')}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-indigo-200 text-indigo-700 text-xs font-semibold hover:bg-indigo-50">
                                    <MessageSquare className="w-3.5 h-3.5" /> Text {shortlist.size > 0 ? 'selected' : 'all'}
                                </button>
                                {isAdmin && (
                                    <button onClick={exportCsv} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 hover:bg-gray-50">
                                        <Download className="w-3.5 h-3.5" /> Export CSV
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {results.count === 0 ? (
                        <div className="text-sm text-gray-500 py-6 text-center">
                            No buyers meet every criterion. Try widening a range (price, beds, baths, cars), ticking more suburbs, or leaving a field blank to ignore it.
                        </div>
                    ) : (
                        <div className="space-y-2.5">
                            {results.results.slice(0, visibleCount).map((b) => {
                                const meta = MATCH_SUBURB_META[b.suburb_match] || MATCH_SUBURB_META.other;
                                const rid = rowId(b);
                                const isOpen = expanded.has(rid);
                                return (
                                    <div key={rid} className={`rounded-xl border ${meta.ring} ${meta.bg}`}>
                                        <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 p-3">
                                            <input type="checkbox" checked={shortlist.has(rid)} onChange={() => toggleShortlist(rid)}
                                                className="rounded border-gray-300 shrink-0" onClick={(e) => e.stopPropagation()} />
                                            {/* match score */}
                                            <div className="text-center shrink-0 w-12">
                                                <div className={`text-xl font-extrabold leading-none ${scoreColor(b.match_score)}`} title={scoreTitle(b.score_parts)}>{b.match_score}</div>
                                                <div className="text-[10px] text-gray-400 uppercase tracking-wide">match</div>
                                            </div>
                                            {/* identity + reasons */}
                                            <button onClick={() => setActionsBuyer(b)} className="flex-1 min-w-0 text-left">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-semibold text-gray-900 truncate">{b.name || b.email || 'Buyer'}</span>
                                                    <MatchLevelChip match={b} />
                                                    {b.source && (
                                                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${(SOURCE_META[b.source] || SOURCE_META.portal).cls}`}>
                                                            {(SOURCE_META[b.source] || SOURCE_META.portal).label}
                                                        </span>
                                                    )}
                                                    <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${meta.text} bg-white/70`}>
                                                        {b.matched_suburb}{b.matched_distance_km != null && b.suburb_match === 'surrounding' ? ` · ${b.matched_distance_km}km` : ''}
                                                    </span>
                                                    {b.intent_score > 0 && (
                                                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                                                            <Sparkles className="w-3 h-3" /> intent {b.intent_score}
                                                        </span>
                                                    )}
                                                    {b.likely_transaction && (
                                                        <span className="text-[11px] font-medium text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Likely 90d</span>
                                                    )}
                                                    {b.latest_feedback && fbBucket(b.latest_feedback) && (
                                                        <span
                                                            title={b.latest_feedback.text || 'Open-home feedback'}
                                                            className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded ${FB_META[fbBucket(b.latest_feedback)].cls}`}
                                                        >
                                                            <MessageSquare className="w-3 h-3" /> {FB_META[fbBucket(b.latest_feedback)].label}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                                    <FitChip ok={b.price_fit === 'in'} near={b.price_fit === 'near'} label={b.price_fit === 'out' ? 'Budget off' : 'Budget'} />
                                                    <FitChip ok={b.beds_fit === 'in'} near={b.beds_fit === 'near'} label={b.beds_fit === 'out' ? 'Beds off' : 'Beds'} />
                                                    <FitChip ok={b.baths_fit === 'in'} near={b.baths_fit === 'near'} label={b.baths_fit === 'out' ? 'Baths off' : 'Baths'} />
                                                    <FitChip ok={b.cars_fit === 'in'} near={b.cars_fit === 'near'} label={b.cars_fit === 'out' ? 'Cars off' : 'Cars'} />
                                                    <FitChip ok={b.type_fit === 'in'} near={false} label={b.type_fit === 'out' ? 'Type differs' : 'Type'} />
                                                    {b.finance_label && <span className="text-[11px] text-gray-500">· {b.finance_label}</span>}
                                                    {b.timeline_label && <span className="text-[11px] text-gray-500">· {b.timeline_label}</span>}
                                                </div>
                                                <NextAction match={b} className="mt-1" />
                                            </button>
                                            {/* quick contact */}
                                            <div className="flex w-full sm:w-auto justify-end items-center gap-1.5 shrink-0">
                                                {b.phone && (
                                                    <a href={`tel:${b.phone}`} onClick={(e) => e.stopPropagation()} title={b.phone}
                                                        className="p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-indigo-600 hover:border-indigo-200">
                                                        <Phone className="w-4 h-4" />
                                                    </a>
                                                )}
                                                {b.email && (
                                                    <a href={`mailto:${b.email}`} onClick={(e) => e.stopPropagation()} title={b.email}
                                                        className="p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-indigo-600 hover:border-indigo-200">
                                                        <Mail className="w-4 h-4" />
                                                    </a>
                                                )}
                                                {/* Same shared notes thread as the property tab, so a note
                                                    written in either place is read in both. */}
                                                <ContactNotesButton
                                                    suggestPreferences
                                                    identity={{ contactId: b.contact_id, buyerId: b.buyer_id, email: b.email, name: b.name, phone: b.phone }}
                                                    compact
                                                    onNoteChange={(note) => applyLatestNote(b, note)}
                                                    className={`p-2 rounded-lg bg-white border ${latestNoteFor(b) ? 'border-amber-300 text-amber-600' : 'border-gray-200 text-gray-600'} hover:text-amber-600 hover:border-amber-300`}
                                                />
                                                <button onClick={(e) => { e.stopPropagation(); setEditBuyer(b); }} title="Edit preferences"
                                                    className="p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-indigo-600 hover:border-indigo-200">
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button onClick={(e) => { e.stopPropagation(); handleMarkPurchased(b); }}
                                                    disabled={purchasingId === rid}
                                                    title="Mark as Recently Purchased"
                                                    className="p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-emerald-600 hover:border-emerald-300 disabled:opacity-50">
                                                    <ShoppingBag className="w-4 h-4" />
                                                </button>
                                                <button onClick={(e) => { e.stopPropagation(); handleMarkRented(b); }}
                                                    disabled={rentingId === rid}
                                                    title="Mark as Recently Rented"
                                                    className="p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-sky-600 hover:border-sky-300 disabled:opacity-50">
                                                    <Home className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => setExpanded((prev) => { const n = new Set(prev); n.has(rid) ? n.delete(rid) : n.add(rid); return n; })}
                                                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600">
                                                    {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </div>
                                        {latestNoteFor(b) && (
                                            <div className="px-3 pb-3 -mt-1">{renderLastNote(b)}</div>
                                        )}
                                        {isOpen && (
                                            <div className="px-3 pb-3 pt-0 text-xs text-gray-600 border-t border-white/60 bg-white/40">
                                                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 pt-2">
                                                    <div><span className="text-gray-400">Wants suburbs:</span> {(b.pref_suburbs || []).join(', ') || '-'}</div>
                                                    <div><span className="text-gray-400">Budget:</span> {fmtMoney(b.pref_price_min) || 'Any'} to {fmtMoney(b.pref_price_max) || 'Any'}</div>
                                                    <div><span className="text-gray-400">Bedrooms:</span> {b.pref_bedrooms_min || 'Any'} to {b.pref_bedrooms_max || 'Any'}</div>
                                                    <div><span className="text-gray-400">Baths / Cars:</span> {b.pref_bathrooms || 'Any'} / {b.pref_car_spaces || 'Any'}</div>
                                                    <div><span className="text-gray-400">Type:</span> {(b.pref_property_type || []).join(', ') || 'Any'}</div>
                                                </div>
                                                {b.latest_feedback && (b.latest_feedback.text || b.latest_feedback.price_feedback) && (
                                                    <div className="mt-2 rounded-md bg-teal-50 border border-teal-100 px-2 py-1.5">
                                                        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-teal-700">
                                                            <MessageSquare className="w-3 h-3" /> Open-home feedback
                                                            {b.latest_feedback.property && <span className="text-gray-500 font-normal normal-case">· {b.latest_feedback.property}</span>}
                                                        </div>
                                                        {b.latest_feedback.text && <p className="text-[11px] text-gray-700 mt-1 whitespace-pre-wrap break-words">"{b.latest_feedback.text}"</p>}
                                                        {b.latest_feedback.price_feedback && <p className="text-[10px] text-gray-500 mt-0.5">Price feedback: {b.latest_feedback.price_feedback}</p>}
                                                    </div>
                                                )}
                                                <div className="mt-2 flex flex-wrap gap-1.5"><ReasonChips match={b} /></div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {visibleCount < totalRows && (
                                <div ref={moreRowsRef} className="pt-2 text-center">
                                    <button
                                        onClick={() => setVisibleCount((c) => Math.min(c + ROWS_PER_BATCH, totalRows))}
                                        className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50">
                                        Show more ({(totalRows - visibleCount).toLocaleString()} still to load)
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* contact_id pins the popup to the very same CRM contact the property
                tab uses. Without it a match with no email (a database record
                carrying only a phone) resolved to nothing and came up empty. */}
            {actionsBuyer && (
                <BuyerActionsModal
                    buyer={{ id: actionsBuyer.buyer_id, contactId: actionsBuyer.contact_id, name: actionsBuyer.name, email: actionsBuyer.email, phone: actionsBuyer.phone }}
                    propertyId={selectedListing?.id}
                    onClose={() => setActionsBuyer(null)}
                    onNoteChange={(note) => applyLatestNote(actionsBuyer, note)}
                    onPurchasedChange={handlePurchasedChange}
                    onRentedChange={moveMatchRow}
                />
            )}

            {editBuyer && (
                <EditBuyerModal
                    buyer={editBuyer}
                    onClose={() => setEditBuyer(null)}
                    onSaved={() => { setEditBuyer(null); runSearch(); }}
                />
            )}

            {showReport && (
                <BuyerDemandReportModal
                    criteria={searchPayload}
                    count={results?.count || 0}
                    propertyLabel={selectedListing?.address || selectedAddress?.description || ''}
                    isAdmin={isAdmin}
                    onClose={() => setShowReport(false)}
                />
            )}

            {messageChannel && (
                <MessageMatchesModal
                    channel={messageChannel}
                    recipients={messageRecipients}
                    propertyLabel={propertyLabel}
                    // Lets the composer resolve the property merge tags
                    // ({{PropertyAddress}}, {{Price}}, {{AuctionDate}}, {{OpenTime}})
                    // for the library's buyer templates.
                    propertyId={selectedListing?.id}
                    currentUser={user}
                    onClose={() => setMessageChannel(null)}
                    onSent={() => setMessageChannel(null)}
                />
            )}

            {rentDialog && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
                    onClick={() => rentingId ? null : setRentDialog(null)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 text-gray-900 font-bold">
                            <Home className="w-4 h-4 text-sky-600" />
                            Mark {rentDialog.buyer?.name} as Recently Rented
                        </div>
                        <p className="mt-2 text-xs text-gray-600">
                            They stop showing as a buyer and stop getting property alerts until the date below. Then they come back as Actively looking and you get a reminder to call them.
                        </p>
                        <p className="mt-4 mb-2 text-[11px] font-semibold text-gray-700">When might they look to buy again?</p>
                        <RentedReturnPicker
                            option={rentDialog.option}
                            date={rentDialog.date}
                            onOptionChange={(option) => setRentDialog((d) => ({ ...d, option }))}
                            onDateChange={(date) => setRentDialog((d) => ({ ...d, date }))}
                        />
                        <div className="mt-5 flex justify-end gap-2">
                            <button type="button" onClick={() => setRentDialog(null)} disabled={!!rentingId}
                                className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-500 hover:text-gray-700 disabled:opacity-60">
                                Cancel
                            </button>
                            <button type="button" onClick={submitMarkRented} disabled={!!rentingId}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-4 py-2 text-sm font-bold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60">
                                {rentingId ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                Mark rented
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {purchaseDialog && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
                    onClick={() => purchasingId ? null : setPurchaseDialog(null)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 text-gray-900 font-bold">
                            <ShoppingBag className="w-4 h-4 text-amber-600" />
                            Mark {purchaseDialog.buyer?.name} as Recently Purchased
                        </div>
                        <p className="mt-2 text-xs text-gray-600">
                            They move to the Purchased tab and stop showing as a buyer. You can undo this from the Purchased tab.
                        </p>
                        <label className="mt-4 mb-1 flex items-center gap-1.5 text-[11px] font-semibold text-gray-700">
                            Date of purchase (optional)
                        </label>
                        <input
                            type="date"
                            value={purchaseDialog.date}
                            max={new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setPurchaseDialog((d) => ({ ...d, date: e.target.value }))}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-amber-400 focus:outline-none"
                        />
                        <div className="mt-5 flex justify-end gap-2">
                            <button type="button" onClick={() => setPurchaseDialog(null)} disabled={!!purchasingId}
                                className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-500 hover:text-gray-700 disabled:opacity-60">
                                Cancel
                            </button>
                            <button type="button" onClick={submitMarkPurchased} disabled={!!purchasingId}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-bold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60">
                                {purchasingId ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                Mark purchased
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
